import type { SVGProps } from 'react';

interface IconProps extends SVGProps<SVGSVGElement> {
  color?: string;
  size?: string | number;
}

const CustomIconKeys = [
  'CheckedCheckbox',
  'ChevronsUpDown',
  'GripVertical',
  'ManyDots',
  'NavigationBlank',
  'NavigationCheck',
  'PanelLeft',
  'JukeboxLogo',
  'JukeboxSecondaryMark',
  'MoneyIcon',
  'MessageCircle',
  'QuoteMark',
  'DOC',
  'PDF',
  'CSV',
  'SecurePlatform',
  'Checkr',
  'HomeDepot',
  'ConstructionPm',
  'Installer',
  'OT',
  'CareCoordinator'
];

const Icons = {
  // Custom Icons
  QuoteMark: ({ className, ...props }: IconProps) => (
    <svg
      className={className}
      fill="none"
      height={41}
      viewBox="0 0 41 35"
      width={35}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path xmlns="http://www.w3.org/2000/svg" d="M4.16 15.72C2.12 19.44 0.92 22.2 0.92 25.44C0.92 30.72 4.76 34.8 9.8 34.8C14.36 34.8 17.96 31.32 17.96 26.52C17.96 21.72 14.12 18.36 9.44 18.36C8.84 18.36 8.36 18.36 7.88 18.48L18.32 0.599994H12.8L4.16 15.72ZM32 18.36C31.52 18.36 31.04 18.36 30.56 18.48L40.88 0.599994H35.36L26.84 15.72C24.8 19.44 23.6 22.2 23.6 25.44C23.6 30.72 27.32 34.8 32.36 34.8C37.04 34.8 40.64 31.32 40.64 26.52C40.64 21.72 36.8 18.36 32 18.36Z" fill="#D8DFD8"/>
    </svg>
  ),
  MessageCircle: ({ className, size, ...props }: IconProps) => (
    <svg
      className={className}
      fill="none"
      height={size || '20'}
      viewBox="0 0 20 20"
      width={size || '20'}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M17.5 9.58336C17.5029 10.6832 17.2459 11.7683 16.75 12.75C16.162 13.9265 15.2581 14.916 14.1395 15.6078C13.021 16.2995 11.7319 16.6662 10.4167 16.6667C9.31678 16.6696 8.23176 16.4126 7.25 15.9167L2.5 17.5L4.08333 12.75C3.58744 11.7683 3.33047 10.6832 3.33333 9.58336C3.33384 8.26815 3.70051 6.97907 4.39227 5.86048C5.08402 4.7419 6.07355 3.838 7.25 3.25002C8.23176 2.75413 9.31678 2.49716 10.4167 2.50002H10.8333C12.5703 2.59585 14.2109 3.32899 15.4409 4.55907C16.671 5.78915 17.4042 7.42973 17.5 9.16669V9.58336Z"
        stroke="#0C4D32"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </svg>
  ),
  MoneyIcon: ({ className, size, ...props }: IconProps) => (
    <svg
      className={className}
      fill="none"
      height={size || '24'}
      viewBox="0 0 24 24"
      width={size || '24'}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g clipPath="url(#clip0_2535_28920)">
        <path
          d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2.33"
        />
        <path
          d="M12 5.5835V18.4168"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
        <path
          d="M14.9167 7.9165H10.5417C10.0002 7.9165 9.48088 8.13161 9.09799 8.51449C8.7151 8.89738 8.5 9.41669 8.5 9.95817C8.5 10.4997 8.7151 11.019 9.09799 11.4018C9.48088 11.7847 10.0002 11.9998 10.5417 11.9998H13.4583C13.9998 11.9998 14.5191 12.2149 14.902 12.5978C15.2849 12.9807 15.5 13.5 15.5 14.0415C15.5 14.583 15.2849 15.1023 14.902 15.4852C14.5191 15.8681 13.9998 16.0832 13.4583 16.0832H8.5"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
      </g>
      <defs>
        <clipPath id="clip0_2535_28920">
          <rect fill="white" height="24" width="24" />
        </clipPath>
      </defs>
    </svg>
  ),
  SecurePlatform: ({ className, size, ...props }: IconProps) => (
    <svg
      className={className}
      fill="none"
      height={size || '40'}
      viewBox="0 0 108 40"
      width={size || '108'}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <rect
        fill="#128632"
        fillOpacity="0.16"
        height="38"
        rx="7"
        stroke="#128632"
        strokeWidth="2"
        width="106"
        x="1"
        y="1"
      />
      <circle
        cx="21"
        cy="20"
        fill="#128632"
        fillOpacity="0.16"
        r="12"
        stroke="#128632"
        strokeWidth="2"
      />
      <g clipPath="url(#clip0_1835_8175)">
        <path
          d="M25.55 19.35H16.45C15.7321 19.35 15.15 19.932 15.15 20.65V25.2C15.15 25.918 15.7321 26.5 16.45 26.5H25.55C26.268 26.5 26.85 25.918 26.85 25.2V20.65C26.85 19.932 26.268 19.35 25.55 19.35Z"
          stroke="#128632"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
        <path
          d="M17.75 19.35V16.75C17.75 15.8881 18.0924 15.0614 18.7019 14.4519C19.3114 13.8424 20.138 13.5 21 13.5C21.862 13.5 22.6886 13.8424 23.2981 14.4519C23.9076 15.0614 24.25 15.8881 24.25 16.75V19.35"
          stroke="#128632"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
      </g>
      <path
        d="M41.408 15.408H42.944C43.052 16.572 44.06 16.968 45.128 16.968C46.148 16.968 46.94 16.512 46.94 15.648C46.94 15.024 46.568 14.676 45.536 14.484L43.964 14.196C42.704 13.968 41.684 13.368 41.684 11.868C41.684 10.344 43.028 9.288 44.9 9.288C46.94 9.288 48.164 10.176 48.332 11.784H46.832C46.724 10.944 45.98 10.476 44.912 10.476C43.88 10.476 43.232 11.016 43.232 11.772C43.232 12.48 43.676 12.708 44.6 12.888L46.256 13.212C47.66 13.476 48.5 14.172 48.5 15.552C48.5 17.112 47.072 18.144 45.176 18.144C43.136 18.144 41.564 17.292 41.408 15.408ZM55.4105 16.104C55.1465 17.412 54.0545 18.144 52.6625 18.144C50.8865 18.144 49.5785 16.956 49.5785 14.964C49.5785 12.972 50.9105 11.76 52.6265 11.76C54.5345 11.76 55.4465 13.212 55.4465 14.832V15.324H51.0665C51.1625 16.404 51.7145 17.016 52.6625 17.016C53.3585 17.016 53.7905 16.692 53.9945 16.104H55.4105ZM52.6265 12.9C51.8225 12.9 51.2825 13.392 51.1145 14.328H53.9825C53.9825 13.572 53.5505 12.9 52.6265 12.9ZM56.5043 14.952C56.5043 12.984 57.7763 11.76 59.5643 11.76C61.0763 11.76 62.0483 12.636 62.2163 13.992H60.7643C60.6443 13.332 60.2483 12.912 59.5523 12.912C58.5803 12.912 58.0403 13.716 58.0403 14.952C58.0403 16.2 58.5803 16.992 59.5523 16.992C60.2483 16.992 60.6443 16.56 60.7643 15.912H62.2163C62.0483 17.28 61.0763 18.144 59.5643 18.144C57.7763 18.144 56.5043 16.932 56.5043 14.952ZM69.124 18H67.612V16.98C67.324 17.676 66.628 18.144 65.716 18.144C64.348 18.144 63.544 17.304 63.544 15.828V11.904H65.092V15.492C65.092 16.464 65.476 16.956 66.268 16.956C67.012 16.956 67.576 16.44 67.576 15.216V11.904H69.124V18ZM74.8385 11.844V13.26C74.6585 13.224 74.4425 13.212 74.2505 13.212C73.3145 13.212 72.5345 13.86 72.5345 15.18V18H70.9985V11.904H72.5105V13.272C72.7985 12.324 73.5065 11.796 74.3825 11.796C74.5385 11.796 74.7065 11.808 74.8385 11.844ZM81.3207 16.104C81.0567 17.412 79.9647 18.144 78.5727 18.144C76.7967 18.144 75.4887 16.956 75.4887 14.964C75.4887 12.972 76.8207 11.76 78.5367 11.76C80.4447 11.76 81.3567 13.212 81.3567 14.832V15.324H76.9767C77.0727 16.404 77.6247 17.016 78.5727 17.016C79.2687 17.016 79.7007 16.692 79.9047 16.104H81.3207ZM78.5367 12.9C77.7327 12.9 77.1927 13.392 77.0247 14.328H79.8927C79.8927 13.572 79.4607 12.9 78.5367 12.9ZM45.524 28.844H43.532V32H41.972V23.432H45.512C47.54 23.432 48.716 24.356 48.716 26.144C48.716 27.932 47.54 28.844 45.524 28.844ZM43.532 27.596H45.38C46.556 27.596 47.144 27.116 47.144 26.144C47.144 25.16 46.556 24.692 45.38 24.692H43.532V27.596ZM51.5697 32H50.0337V23.168H51.5697V32ZM57.1638 32C57.1038 31.664 57.0798 31.34 57.0798 31.04C56.7918 31.736 56.0238 32.132 55.0518 32.132C53.8638 32.132 52.9998 31.472 52.9998 30.368C52.9998 29.144 53.9598 28.64 55.1958 28.496L57.0198 28.292V28.004C57.0198 27.26 56.5638 26.9 55.8678 26.9C55.1958 26.9 54.7878 27.272 54.7398 27.836H53.2638C53.3958 26.528 54.4158 25.76 55.9158 25.76C57.5718 25.76 58.5198 26.54 58.5198 28.124V30.476C58.5198 31.04 58.5438 31.508 58.6398 32H57.1638ZM55.4718 31.064C56.2638 31.064 57.0198 30.596 57.0198 29.636V29.264L55.6638 29.42C55.0158 29.504 54.5238 29.648 54.5238 30.284C54.5238 30.764 54.8598 31.064 55.4718 31.064ZM63.5624 30.74V31.916C63.0824 32.06 62.7224 32.096 62.3504 32.096C61.0784 32.096 60.4544 31.556 60.4544 30.332V27.008H59.6144V25.904H60.4544V24.38H61.9784V25.904H63.4304V27.008H61.9664V30.092C61.9664 30.668 62.2424 30.86 62.7584 30.86C62.9864 30.86 63.2264 30.836 63.5624 30.74ZM65.1665 27.008H64.3265V25.904H65.1665V25.04C65.1665 23.78 65.9225 23.132 67.1585 23.132C67.4825 23.132 67.7945 23.144 68.0465 23.192V24.356C67.8665 24.332 67.6745 24.32 67.4585 24.32C67.0145 24.32 66.7025 24.524 66.7025 25.112V25.904H67.9385V27.008H66.7025V32H65.1665V27.008ZM74.8129 28.952C74.8129 30.932 73.5289 32.144 71.7289 32.144C69.9289 32.144 68.6449 30.932 68.6449 28.952C68.6449 26.984 69.9289 25.76 71.7289 25.76C73.5289 25.76 74.8129 26.984 74.8129 28.952ZM70.1809 28.952C70.1809 30.236 70.7329 31.004 71.7289 31.004C72.7129 31.004 73.2769 30.236 73.2769 28.952C73.2769 27.68 72.7129 26.9 71.7289 26.9C70.7329 26.9 70.1809 27.68 70.1809 28.952ZM80.1002 25.844V27.26C79.9202 27.224 79.7042 27.212 79.5122 27.212C78.5762 27.212 77.7962 27.86 77.7962 29.18V32H76.2602V25.904H77.7722V27.272C78.0602 26.324 78.7682 25.796 79.6442 25.796C79.8002 25.796 79.9682 25.808 80.1002 25.844ZM86.5438 26.948C86.9278 26.216 87.6958 25.76 88.5958 25.76C89.9998 25.76 90.7438 26.636 90.7438 28.088V32H89.1958V28.424C89.1958 27.452 88.8358 26.972 88.0798 26.972C87.3478 26.972 86.7718 27.488 86.7718 28.676V32H85.2358V28.424C85.2358 27.452 84.8758 26.972 84.1198 26.972C83.3758 26.972 82.8118 27.488 82.8118 28.676V32H81.2758V25.904H82.7878V26.936C83.0758 26.228 83.7838 25.76 84.6598 25.76C85.6798 25.76 86.2438 26.228 86.5438 26.948Z"
        fill="#128632"
      />
      <defs>
        <clipPath id="clip0_1835_8175">
          <rect fill="white" height="15.6" transform="translate(13.2 12.2)" width="15.6" />
        </clipPath>
      </defs>
    </svg>
  ),
  Checkr: ({ className, size, ...props }: IconProps) => (
    <svg
      className={className}
      fill="none"
      height={size ? 'auto' : '45'}
      viewBox="0 0 86 20"
      width={size || '98'}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M37.5225 4.64389C33.298 4.64389 30.1121 7.87977 30.1121 12.1728C30.1121 16.4658 33.298 19.7002 37.5225 19.7002C40.429 19.7002 42.9191 18.1797 44.1875 15.6336L44.2446 15.5196L40.5807 14.2526L40.5342 14.3335C39.8819 15.4821 38.8961 16.058 37.5195 16.0939C35.7702 16.1374 34.577 15.1418 34.2208 13.3484H44.8322L44.8443 13.2494C44.8938 12.8641 44.9179 12.4847 44.9194 12.1218C44.9194 7.85878 41.7394 4.64389 37.524 4.64389H37.5225ZM34.3335 10.5309C34.7964 8.98939 35.9505 8.10919 37.5225 8.10919C39.0944 8.10919 40.3042 8.9669 40.8467 10.5309H34.3335Z"
        fill="#808080"
      />
      <path
        d="M56.583 13.6318C55.9518 15.0983 54.7857 15.9065 53.2979 15.9065C51.3352 15.9065 49.7963 14.2571 49.7963 12.1503C49.7963 10.0435 51.3021 8.43608 53.2979 8.43608C54.7631 8.43608 55.9939 9.22631 56.589 10.5504L56.6311 10.6448L60.28 9.38376L60.2394 9.27579C59.1694 6.41778 56.5184 4.6424 53.3189 4.6424C48.9832 4.6424 45.7146 7.86028 45.7146 12.1278C45.7146 14.1941 46.4886 16.1074 47.8952 17.514C49.3033 18.9235 51.2225 19.6987 53.2964 19.6987C56.5425 19.6987 59.222 17.8693 60.292 14.9228L60.3311 14.8149L56.6221 13.5343L56.58 13.6303L56.583 13.6318Z"
        fill="#808080"
      />
      <path
        d="M10.8684 13.6318C10.2372 15.0983 9.06955 15.9065 7.58326 15.9065C5.62057 15.9065 4.08167 14.2571 4.08167 12.1503C4.08167 10.0435 5.5875 8.43608 7.58326 8.43608C9.04851 8.43608 10.2793 9.22631 10.8744 10.5504L10.9165 10.6448L14.5654 9.38376L14.5248 9.27579C13.4548 6.41778 10.8038 4.6424 7.6043 4.6424C3.26865 4.64389 0 7.86178 0 12.1293C0 14.1956 0.773955 16.1089 2.1806 17.5154C3.58875 18.925 5.50785 19.7002 7.58175 19.7002C10.8279 19.7002 13.5089 17.8708 14.5774 14.9243L14.6165 14.8164L10.9075 13.5358L10.8654 13.6318H10.8684Z"
        fill="#808080"
      />
      <path
        d="M82.2601 4.6814C80.8745 4.6814 79.1973 5.5406 78.3558 7.02359C78.3242 7.08057 78.2415 7.07457 78.219 7.0131L77.5743 5.07127L77.5698 5.05927H74.9729V19.2834H78.9644V11.8969C78.9644 9.52323 79.4904 8.23967 81.6019 8.08822C82.2947 8.03874 83.518 8.08822 83.6036 8.08822V4.6799H82.2601V4.6814Z"
        fill="#808080"
      />
      <path
        d="M73.9405 5.05925H74.0908H69.4786L65.5412 10.4319C65.4976 10.4949 65.4 10.4634 65.4 10.3884V0H61.4084V19.2833H65.4V13.4204C65.4 13.3169 65.5337 13.2764 65.5923 13.3604L69.7296 19.2399L69.7581 19.2818H74.45L69.1164 11.8354L73.942 5.05775L73.9405 5.05925Z"
        fill="#808080"
      />
      <path
        d="M23.8379 4.66189C22.2464 4.66189 20.5362 5.29167 19.8555 6.64571C19.8119 6.73118 19.6841 6.70269 19.6841 6.60672V0H15.6926V19.2833H19.6841V11.8114C19.6841 9.61168 20.8488 8.08821 22.7379 8.08821C24.3474 8.08821 25.1063 9.21432 25.1063 11.2956V19.2818H29.0978V10.6598C29.0978 7.01458 27.0329 4.66039 23.8379 4.66039V4.66189Z"
        fill="#808080"
      />
      <path
        d="M84.7993 19.2833C84.5799 19.2833 84.3785 19.2309 84.1952 19.1244C84.0118 19.0194 83.8676 18.8755 83.7609 18.6955C83.6542 18.5156 83.6016 18.3177 83.6016 18.1047C83.6016 17.8918 83.6542 17.6924 83.7609 17.511C83.8676 17.3295 84.0118 17.1871 84.1952 17.0821C84.3785 16.9771 84.5799 16.9247 84.7993 16.9247C85.0187 16.9247 85.2201 16.9771 85.4034 17.0821C85.5868 17.1871 85.7326 17.3295 85.8393 17.511C85.946 17.6924 86.0001 17.8903 86.0001 18.1047C86.0001 18.3192 85.946 18.5141 85.8393 18.694C85.7326 18.8755 85.5868 19.0179 85.4034 19.1244C85.2201 19.2294 85.0187 19.2833 84.7993 19.2833ZM84.7993 19.1484C84.9917 19.1484 85.1675 19.1019 85.3283 19.0104C85.4891 18.919 85.6154 18.793 85.71 18.6341C85.8032 18.4751 85.8513 18.2982 85.8513 18.1062C85.8513 17.9143 85.8047 17.7344 85.71 17.5754C85.6169 17.4165 85.4891 17.2905 85.3283 17.1991C85.1675 17.1076 84.9917 17.0611 84.7993 17.0611C84.607 17.0611 84.4326 17.1076 84.2733 17.1991C84.114 17.2905 83.9863 17.4165 83.8931 17.5754C83.7999 17.7344 83.7518 17.9128 83.7518 18.1062C83.7518 18.2997 83.7984 18.4766 83.8931 18.6356C83.9863 18.7945 84.114 18.9205 84.2733 19.0119C84.4326 19.1034 84.6085 19.1484 84.7993 19.1484ZM84.3034 18.865V17.322H84.9015C84.9902 17.322 85.0698 17.343 85.1435 17.385C85.2171 17.427 85.2742 17.484 85.3178 17.5559C85.3599 17.6279 85.3809 17.7074 85.3809 17.7944C85.3809 17.8918 85.3539 17.9803 85.3013 18.0598C85.2472 18.1377 85.178 18.1947 85.0909 18.2277L85.4095 18.865H85.2351L84.9376 18.2622C84.9271 18.2622 84.915 18.2637 84.9015 18.2652C84.8895 18.2652 84.876 18.2667 84.8624 18.2667H84.4627V18.8635H84.3019L84.3034 18.865ZM84.894 18.1347C84.9541 18.1347 85.0097 18.1197 85.0608 18.0883C85.1104 18.0568 85.151 18.0163 85.1795 17.9653C85.2081 17.9143 85.2231 17.8573 85.2231 17.7959C85.2231 17.7344 85.2081 17.6804 85.1795 17.6279C85.151 17.5754 85.1104 17.5334 85.0593 17.5035C85.0082 17.472 84.9526 17.457 84.8925 17.457H84.4642V18.1332H84.894V18.1347Z"
        fill="#808080"
      />
    </svg>
  ),
  HomeDepot: ({ className, size, ...props }: IconProps) => (
    <svg
      className={className}
      fill="none"
      height={size || '40'}
      viewBox="0 0 132 40"
      width={size || '132'}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M52.516 17H48.134V7.004H52.152C54.49 7.004 55.764 7.942 55.764 9.734C55.764 10.616 55.064 11.582 54.252 11.82C55.344 12.114 56.17 12.996 56.17 14.158C56.17 16.034 54.826 17 52.516 17ZM49.954 11.232H52.138C53.384 11.232 53.972 10.672 53.972 9.804C53.972 8.894 53.37 8.376 52.152 8.376H49.954V11.232ZM49.954 12.562V15.628H52.376C53.664 15.628 54.336 15.054 54.336 14.088C54.336 13.15 53.65 12.562 52.362 12.562H49.954ZM62.3304 17C62.2604 16.608 62.2324 16.23 62.2324 15.88C61.8964 16.692 61.0004 17.154 59.8664 17.154C58.4804 17.154 57.4724 16.384 57.4724 15.096C57.4724 13.668 58.5924 13.08 60.0344 12.912L62.1624 12.674V12.338C62.1624 11.47 61.6304 11.05 60.8184 11.05C60.0344 11.05 59.5584 11.484 59.5024 12.142H57.7804C57.9344 10.616 59.1244 9.72 60.8744 9.72C62.8064 9.72 63.9124 10.63 63.9124 12.478V15.222C63.9124 15.88 63.9404 16.426 64.0524 17H62.3304ZM60.3564 15.908C61.2804 15.908 62.1624 15.362 62.1624 14.242V13.808L60.5804 13.99C59.8244 14.088 59.2504 14.256 59.2504 14.998C59.2504 15.558 59.6424 15.908 60.3564 15.908ZM65.5395 13.444C65.5395 11.148 67.0235 9.72 69.1095 9.72C70.8735 9.72 72.0075 10.742 72.2035 12.324H70.5095C70.3695 11.554 69.9075 11.064 69.0955 11.064C67.9615 11.064 67.3315 12.002 67.3315 13.444C67.3315 14.9 67.9615 15.824 69.0955 15.824C69.9075 15.824 70.3695 15.32 70.5095 14.564H72.2035C72.0075 16.16 70.8735 17.168 69.1095 17.168C67.0235 17.168 65.5395 15.754 65.5395 13.444ZM78.7785 17L76.4405 13.99L75.6145 14.788V17H73.8225V6.696H75.6145V12.94L78.6385 9.888H80.6965L77.6865 12.856L80.8365 17H78.7785ZM88.2576 14.788C87.9496 16.314 86.6756 17.168 85.0516 17.168C82.9796 17.168 81.4536 15.782 81.4536 13.458C81.4536 11.134 83.0076 9.72 85.0096 9.72C87.2356 9.72 88.2996 11.414 88.2996 13.304V13.878H83.1896C83.3016 15.138 83.9456 15.852 85.0516 15.852C85.8636 15.852 86.3676 15.474 86.6056 14.788H88.2576ZM85.0096 11.05C84.0716 11.05 83.4416 11.624 83.2456 12.716H86.5916C86.5916 11.834 86.0876 11.05 85.0096 11.05ZM94.9516 17V15.866C94.6016 16.58 93.7476 17.168 92.6696 17.168C90.8076 17.168 89.5336 15.656 89.5336 13.444C89.5336 11.246 90.8076 9.72 92.6696 9.72C93.7756 9.72 94.5876 10.336 94.9236 11.05V6.696H96.7156V17H94.9516ZM94.9656 13.57V13.318C94.9656 11.932 94.3076 11.036 93.1736 11.036C92.0116 11.036 91.3396 11.988 91.3396 13.444C91.3396 14.914 92.0116 15.852 93.1736 15.852C94.3076 15.852 94.9656 14.956 94.9656 13.57ZM103.6 15.866V17H101.836V6.696H103.628V11.05C103.964 10.336 104.776 9.72 105.882 9.72C107.744 9.72 109.018 11.246 109.018 13.444C109.018 15.656 107.744 17.168 105.882 17.168C104.804 17.168 103.95 16.58 103.6 15.866ZM103.586 13.318V13.57C103.586 14.956 104.244 15.852 105.378 15.852C106.54 15.852 107.212 14.914 107.212 13.444C107.212 11.988 106.54 11.036 105.378 11.036C104.244 11.036 103.586 11.932 103.586 13.318ZM112.45 16.986L109.678 9.888H111.498L113.472 15.25H113.5L115.376 9.888H117.154L114.382 17.126C113.71 18.904 112.884 19.59 111.386 19.604C111.148 19.604 110.812 19.576 110.56 19.534V18.176C110.77 18.218 110.882 18.218 111.036 18.218C111.764 18.218 112.072 17.966 112.45 16.986ZM48.134 34V24.004H49.996V28.204H55.12V24.004H56.968V34H55.12V29.66H49.996V34H48.134ZM65.8996 30.444C65.8996 32.754 64.4016 34.168 62.3016 34.168C60.2016 34.168 58.7036 32.754 58.7036 30.444C58.7036 28.148 60.2016 26.72 62.3016 26.72C64.4016 26.72 65.8996 28.148 65.8996 30.444ZM60.4956 30.444C60.4956 31.942 61.1396 32.838 62.3016 32.838C63.4496 32.838 64.1076 31.942 64.1076 30.444C64.1076 28.96 63.4496 28.05 62.3016 28.05C61.1396 28.05 60.4956 28.96 60.4956 30.444ZM73.7341 28.106C74.1821 27.252 75.0781 26.72 76.1281 26.72C77.7661 26.72 78.6341 27.742 78.6341 29.436V34H76.8281V29.828C76.8281 28.694 76.4081 28.134 75.5261 28.134C74.6721 28.134 74.0001 28.736 74.0001 30.122V34H72.2081V29.828C72.2081 28.694 71.7881 28.134 70.9061 28.134C70.0381 28.134 69.3801 28.736 69.3801 30.122V34H67.5881V26.888H69.3521V28.092C69.6881 27.266 70.5141 26.72 71.5361 26.72C72.7261 26.72 73.3841 27.266 73.7341 28.106ZM87.0544 31.788C86.7464 33.314 85.4724 34.168 83.8484 34.168C81.7764 34.168 80.2504 32.782 80.2504 30.458C80.2504 28.134 81.8044 26.72 83.8064 26.72C86.0324 26.72 87.0964 28.414 87.0964 30.304V30.878H81.9864C82.0984 32.138 82.7424 32.852 83.8484 32.852C84.6604 32.852 85.1644 32.474 85.4024 31.788H87.0544ZM83.8064 28.05C82.8684 28.05 82.2384 28.624 82.0424 29.716H85.3884C85.3884 28.834 84.8844 28.05 83.8064 28.05ZM95.6246 24.004C98.6346 24.004 100.623 26.006 100.623 29.002C100.623 32.012 98.6346 34 95.6246 34H91.7746V24.004H95.6246ZM95.6246 25.474H93.5946V32.53H95.6246C97.6126 32.53 98.7186 31.228 98.7186 29.002C98.7186 26.776 97.6126 25.474 95.6246 25.474ZM108.601 31.788C108.293 33.314 107.019 34.168 105.395 34.168C103.323 34.168 101.797 32.782 101.797 30.458C101.797 28.134 103.351 26.72 105.353 26.72C107.579 26.72 108.643 28.414 108.643 30.304V30.878H103.533C103.645 32.138 104.289 32.852 105.395 32.852C106.207 32.852 106.711 32.474 106.949 31.788H108.601ZM105.353 28.05C104.415 28.05 103.785 28.624 103.589 29.716H106.935C106.935 28.834 106.431 28.05 105.353 28.05ZM112.132 32.866V36.492H110.34V26.888H112.104V28.064C112.44 27.35 113.28 26.72 114.386 26.72C116.248 26.72 117.522 28.246 117.522 30.444C117.522 32.656 116.248 34.168 114.386 34.168C113.308 34.168 112.496 33.58 112.132 32.866ZM112.09 30.318V30.57C112.09 31.956 112.748 32.852 113.882 32.852C115.044 32.852 115.716 31.914 115.716 30.444C115.716 28.988 115.044 28.036 113.882 28.036C112.748 28.036 112.09 28.932 112.09 30.318ZM125.946 30.444C125.946 32.754 124.448 34.168 122.348 34.168C120.248 34.168 118.75 32.754 118.75 30.444C118.75 28.148 120.248 26.72 122.348 26.72C124.448 26.72 125.946 28.148 125.946 30.444ZM120.542 30.444C120.542 31.942 121.186 32.838 122.348 32.838C123.496 32.838 124.154 31.942 124.154 30.444C124.154 28.96 123.496 28.05 122.348 28.05C121.186 28.05 120.542 28.96 120.542 30.444ZM131.401 32.53V33.902C130.841 34.07 130.421 34.112 129.987 34.112C128.503 34.112 127.775 33.482 127.775 32.054V28.176H126.795V26.888H127.775V25.11H129.553V26.888H131.247V28.176H129.539V31.774C129.539 32.446 129.861 32.67 130.463 32.67C130.729 32.67 131.009 32.642 131.401 32.53Z"
        fill="#4D4D4D"
      />
      <g clipPath="url(#clip0_1931_7633)">
        <path d="M0.0915527 0.146454H38.9742V39.8512H0.0915527V0.146454Z" fill="white" />
        <path
          d="M0.0913712 0.146318H24.4875C26.4514 0.146318 28.4155 0.142107 30.3753 0.150353C29.8322 0.71316 29.2768 1.27193 28.7299 1.83491C28.946 2.06825 29.1747 2.28509 29.3912 2.51439C29.8894 2.16421 30.4528 1.88491 31.0653 1.83895C31.6164 1.78053 32.1719 1.92649 32.678 2.15176C32.8699 2.23509 33.0496 2.36842 33.2659 2.3893C33.4783 2.37684 33.6376 2.18509 33.7111 1.99316C33.7722 1.86 33.715 1.70141 33.6132 1.60544C33.1395 1.11772 32.6617 0.638247 32.1921 0.150353C34.4541 0.137897 36.7161 0.150353 38.9742 0.146318C38.9742 2.39772 38.9781 4.65334 38.9742 6.90895C38.5088 6.44211 38.0554 5.96667 37.5939 5.49983C37.4714 5.37035 37.2716 5.30369 37.1041 5.38298C36.9938 5.4372 36.8876 5.52474 36.8386 5.64562C36.7773 5.88755 36.8837 6.12509 36.9775 6.34176C37.2633 6.95895 37.39 7.65527 37.3042 8.33491C37.247 8.79772 37.0836 9.25211 36.8386 9.64825C36.7734 9.74404 36.7122 9.84404 36.655 9.94439C36.8917 10.1526 37.0919 10.4028 37.3328 10.6072C37.8717 10.0402 38.427 9.48562 38.9742 8.92263C38.9742 12.6875 38.9781 16.4526 38.9742 20.2175C38.8678 20.2551 38.7495 20.3302 38.6392 20.2758C38.4923 20.18 38.382 20.0382 38.2595 19.9174C36.9366 18.5747 35.6259 17.2197 34.299 15.8814C33.7722 16.4193 33.2539 16.9612 32.7187 17.4867C33.0007 17.8118 33.3151 18.0995 33.609 18.4121C34.6706 19.4963 35.732 20.5804 36.7979 21.6642C36.8876 21.7602 36.9938 21.8437 37.0427 21.9688C37.0591 22.1063 36.9653 22.223 36.9285 22.3481C36.8754 22.494 36.9898 22.6691 37.1409 22.6732C37.2755 22.6984 37.39 22.6065 37.4757 22.5149C37.9738 22.0019 38.476 21.4933 38.9742 20.9846V39.8511H20.4942C20.9679 39.3675 21.4415 38.8879 21.9152 38.4042C20.3031 36.7622 18.693 35.1181 17.0849 33.4719C16.5583 33.9889 16.052 34.5268 15.5294 35.0479C15.4191 35.1647 15.2966 35.2772 15.2436 35.4356C15.2191 35.5898 15.3579 35.7358 15.5048 35.7442C15.6355 35.7275 15.7457 35.6398 15.8764 35.6107C15.9907 35.6026 16.0807 35.6942 16.1621 35.7693C17.1177 36.7532 18.0772 37.729 19.0368 38.7088C19.2775 38.967 19.539 39.2005 19.7633 39.4675C19.8656 39.5842 19.7962 39.7342 19.7389 39.8511H8.68632L11.2952 37.1868C11.4584 37.0284 11.6259 36.8451 11.6503 36.6032C11.6871 36.3239 11.4096 36.0444 11.1359 36.1028C10.9073 36.1486 10.7196 36.3321 10.4827 36.3321C10.3317 36.3197 10.2173 36.207 10.1152 36.107C8.03689 33.9889 5.96285 31.8668 3.88863 29.7486C3.74981 29.607 3.58247 29.4695 3.53334 29.2649C3.52526 29.0233 3.6926 28.8272 3.75393 28.6063C3.83142 28.327 3.57423 28.0225 3.29641 28.0435C3.10468 28.056 2.93322 28.1684 2.80264 28.3061C1.90427 29.236 0.989746 30.1532 0.0913712 31.083C0.0872478 20.7719 0.0913712 10.4572 0.0913712 0.146318Z"
          fill="#F96302"
        />
        <path
          d="M28.1377 2.43527C28.1745 2.39369 28.2397 2.35597 28.2765 2.4186C31.1231 5.32116 33.9676 8.22578 36.8101 11.1325C35.8115 12.1515 34.8139 13.1716 33.8173 14.1928C33.6865 14.3179 33.5273 14.4221 33.3477 14.4346C33.0986 14.4556 32.8495 14.2261 32.8699 13.9675C32.8782 13.7133 33.0903 13.5174 33.0945 13.2588C33.1027 13.0961 32.9802 12.9754 32.8782 12.8626C30.7672 10.7114 28.6603 8.55597 26.5493 6.40018C26.431 6.28351 26.3004 6.13755 26.1207 6.15018C25.8552 6.15421 25.6553 6.40439 25.3859 6.37527C25.2306 6.3793 25.1039 6.25421 25.0265 6.12509C24.9203 5.9 25.0182 5.62895 25.1776 5.46228C26.1657 4.45316 27.1538 3.44421 28.1377 2.43527ZM32.2372 3.28158C32.3188 3.12737 32.4739 2.98562 32.6576 3.01053C32.8617 3.02737 33.0087 3.19807 33.1434 3.33579C33.9591 4.17206 34.7758 5.00733 35.5934 5.84158C35.7485 5.99597 35.9322 6.14597 35.969 6.37527C35.9894 6.61281 35.7771 6.82544 35.5525 6.85053C35.3647 6.85474 35.1933 6.75053 35.03 6.65878C34.6828 6.45035 34.2909 6.28351 33.8784 6.30439C33.6661 6.30018 33.4621 6.38772 33.2701 6.47527C33.0455 6.2293 32.7883 6.00842 32.576 5.75404C32.6412 5.56649 32.727 5.3872 32.7393 5.18685C32.768 4.73263 32.5883 4.29895 32.3636 3.91948C32.2494 3.73193 32.1595 3.49825 32.2372 3.28158ZM14.243 5.49141C14.6842 5.01614 15.1497 4.57 15.5905 4.09878C16.0111 4.51562 16.4193 4.93685 16.8318 5.3579C16.9665 5.49141 16.844 5.71649 16.693 5.77071C16.5664 5.78737 16.4561 5.69983 16.3418 5.65807C15.901 5.4372 15.3538 5.41228 14.9129 5.65404C14.7985 5.70404 14.7045 5.80404 14.582 5.82492C14.4595 5.72492 14.3575 5.59983 14.243 5.49141ZM13.9656 5.75807C14.5657 6.35018 15.1455 6.96316 15.7376 7.5593C16.6645 8.50158 17.5871 9.45228 18.5181 10.3946C17.9876 10.9425 17.4541 11.4873 16.9175 12.029C16.8072 12.1539 16.5868 12.2082 16.4806 12.0539C16.3501 11.9121 16.4928 11.737 16.5337 11.5911C16.5827 11.4826 16.4971 11.3868 16.4276 11.3119C15.3586 10.2243 14.2915 9.13465 13.2265 8.04299C13.1285 7.95123 13.0468 7.83878 12.9284 7.77614C12.7652 7.7386 12.6385 7.88878 12.4793 7.8972C12.3568 7.88456 12.2425 7.75965 12.2589 7.63018C12.2669 7.53018 12.3241 7.44264 12.3935 7.37176C12.9162 6.83386 13.4471 6.30439 13.9656 5.75807ZM16.0968 6.42527C16.0439 6.28772 16.1377 6.12106 16.2807 6.10018C16.3868 6.08351 16.4726 6.16667 16.5461 6.23772C16.987 6.6879 17.4278 7.14246 17.873 7.5886C17.9504 7.67614 18.0607 7.75527 18.069 7.88456C18.0853 8.01386 17.9628 8.13895 17.8322 8.12632C17.6606 8.12632 17.53 7.98878 17.3749 7.93035C17.1462 7.82211 16.8686 7.81369 16.644 7.93878C16.5257 7.8179 16.4071 7.69281 16.2846 7.58018C16.3379 7.39667 16.3868 7.20088 16.3418 7.00895C16.3092 6.79228 16.1621 6.62544 16.0968 6.42527ZM23.9159 6.75053C24.0262 6.6379 24.1648 6.55456 24.3202 6.53369C24.5935 6.49211 24.8794 6.76299 24.8265 7.04649C24.7897 7.29667 24.5856 7.49667 24.6018 7.75965C24.6426 7.94281 24.7897 8.0679 24.9079 8.19737C26.9945 10.3237 29.0809 12.4458 31.1591 14.5763C31.2735 14.6807 31.3878 14.8182 31.5511 14.8182C31.7961 14.8182 31.9839 14.6307 32.2167 14.5888C32.4861 14.5432 32.7515 14.814 32.7187 15.0893C32.7026 15.2768 32.5923 15.4395 32.4658 15.5686C31.4163 16.636 30.371 17.7118 29.3218 18.7791C29.1871 18.9167 29.0156 19.0418 28.8156 19.0418C28.5949 19.0542 28.3827 18.8626 28.3542 18.6416C28.3255 18.3497 28.5909 18.1328 28.5827 17.8409C28.5705 17.7033 28.4767 17.5909 28.3868 17.4907C26.5535 15.623 24.7243 13.7509 22.8911 11.883C24.4059 14.7556 25.9329 17.6202 27.4477 20.4928C27.4721 20.5344 27.4926 20.5804 27.513 20.6304C27.4029 20.7344 27.2967 20.847 27.1905 20.9597C24.1568 19.4339 21.1271 17.8909 18.0975 16.3609C17.6199 16.1191 17.1462 15.8356 16.6073 15.769C16.4276 15.7437 16.3051 15.894 16.1745 15.9898C16.0396 16.0982 15.8599 16.1649 15.6927 16.1107C15.4926 16.0191 15.3538 15.7772 15.4151 15.5561C15.4558 15.4061 15.5579 15.2811 15.664 15.1725C16.644 14.1761 17.6199 13.1756 18.5999 12.1832C20.1267 12.9461 21.6579 13.6925 23.1892 14.4556C23.2381 14.493 23.291 14.5305 23.3361 14.5805C23.3606 14.5512 23.3811 14.5221 23.4055 14.493L23.3483 14.5012C22.544 12.9377 21.6989 11.3951 20.8943 9.83158C21.907 8.80597 22.9074 7.77614 23.9159 6.75053ZM18.5549 7.45509C18.6284 7.31755 18.8366 7.30088 18.9346 7.41755C19.3469 7.84281 19.7676 8.26807 20.1839 8.69351C19.7188 9.1732 19.2507 9.64988 18.7796 10.1235C18.6734 9.99842 18.5303 9.89842 18.4487 9.75246C18.5834 9.51895 18.7263 9.28123 18.7631 9.00614C18.8325 8.62667 18.7713 8.22649 18.6121 7.87632C18.5671 7.74281 18.4691 7.5886 18.5549 7.45509ZM11.6995 8.08053C11.7811 7.99299 11.8709 7.90123 11.9893 7.87211C12.1078 7.8472 12.2547 7.92211 12.2711 8.05544C12.2997 8.20141 12.1936 8.32246 12.165 8.46404C12.1486 8.57667 12.2384 8.65597 12.3037 8.73106C13.394 9.84404 14.48 10.9574 15.5702 12.0663C15.6438 12.1374 15.7213 12.2249 15.8274 12.2498C15.958 12.2374 16.0681 12.1498 16.1949 12.1207C16.3501 12.1039 16.4928 12.2749 16.4439 12.4249C16.4236 12.5375 16.3379 12.6211 16.2643 12.7002C15.7335 13.2423 15.2026 13.7842 14.6719 14.3221C14.5781 14.4181 14.4556 14.5181 14.3166 14.4847C14.2391 14.4512 14.1738 14.3888 14.1451 14.3097C14.1044 14.1261 14.2963 13.976 14.2513 13.7925C14.2106 13.7218 14.1534 13.6674 14.1003 13.6091C13.6022 13.1046 13.1081 12.5918 12.6059 12.0874C12.431 12.2701 12.254 12.4507 12.075 12.6293C11.9525 12.5084 11.83 12.3874 11.7158 12.2582C11.8953 12.079 12.0711 11.8956 12.2508 11.7161C11.8096 11.2744 11.3727 10.8239 10.9359 10.3777C10.842 10.2861 10.7604 10.1861 10.6501 10.1195C10.4501 10.0526 10.2582 10.3404 10.0663 10.1819C9.91095 10.0695 9.96009 9.84404 10.0867 9.72737C10.6215 9.17281 11.1644 8.63088 11.6995 8.08053Z"
          fill="white"
        />
        <path
          d="M9.60891 10.2446C9.73158 10.1651 9.91524 10.2153 9.97228 10.357C10.0295 10.5279 9.87848 10.6739 9.87435 10.8407C9.87023 10.9282 9.93964 10.9907 9.99273 11.0491C11.1237 12.2042 12.2508 13.3589 13.3818 14.5097C13.5857 14.7347 13.8472 14.347 14.0676 14.5139C14.2106 14.6307 14.1616 14.8598 14.0391 14.9682C13.5082 15.5146 12.9734 16.0523 12.4466 16.5984C12.3609 16.6819 12.2752 16.7819 12.1568 16.8195C12.0261 16.857 11.8668 16.7735 11.8506 16.6319C11.8261 16.4653 11.9731 16.3358 11.9649 16.1732C11.9443 16.1023 11.8996 16.044 11.8506 15.9898C10.7801 14.901 9.71161 13.81 8.64525 12.7168C8.54337 12.6211 8.45764 12.5 8.32277 12.45C8.17588 12.4418 8.05338 12.5544 7.91044 12.5709C7.78794 12.5751 7.68176 12.4625 7.67764 12.3418C7.66544 12.2123 7.7553 12.1082 7.84103 12.0207C8.37174 11.4786 8.90245 10.9365 9.42937 10.3946C9.48641 10.3404 9.5395 10.2861 9.60891 10.2446ZM6.65694 13.2256C6.90193 12.9837 7.13868 12.7335 7.38368 12.4875C7.81251 12.913 8.23292 13.3547 8.66174 13.7842C8.73528 13.8675 8.82496 13.9426 8.86568 14.0511C8.91482 14.2554 8.68631 14.4805 8.48616 14.3888C8.286 14.297 8.12691 14.1344 7.93483 14.026C7.61235 13.8218 7.23661 13.6925 6.85297 13.7384C6.74267 13.6218 6.62017 13.5132 6.52224 13.3882C6.55471 13.3214 6.60797 13.2756 6.65694 13.2256ZM4.87668 15.0433C5.33403 14.5847 5.78313 14.1219 6.23636 13.6591C7.09446 14.5395 7.95458 15.4179 8.81671 16.2942C9.29038 16.7735 9.75186 17.2616 10.2295 17.7326C10.3643 17.8868 10.5439 17.7577 10.6909 17.7033C10.8378 17.6367 11.0095 17.7702 11.0054 17.9284C11.0054 18.0368 10.944 18.1286 10.8748 18.2039C10.3195 18.7667 9.7723 19.3295 9.22115 19.8925C9.14349 19.9716 9.04539 20.0507 8.93114 20.0425C8.80452 20.0551 8.68202 19.93 8.69422 19.8047C8.70246 19.6714 8.80452 19.563 8.80452 19.4295C8.81276 19.3168 8.71878 19.246 8.6535 19.1709C7.35104 17.8493 6.06077 16.5153 4.75419 15.1975C4.79508 15.1433 4.83184 15.0933 4.87668 15.0433ZM35.5199 14.6347C36.0261 15.1434 36.5284 15.6562 37.0266 16.1732C37.1857 16.3275 37.0714 16.636 36.8591 16.6695C36.6631 16.6986 36.512 16.5402 36.361 16.436C35.9568 16.1523 35.479 15.8856 34.9728 15.9732C34.838 15.8481 34.7074 15.7188 34.5971 15.573C34.9115 15.2644 35.2137 14.9474 35.5199 14.6347ZM4.46023 15.4811C4.61141 15.5395 4.70522 15.7146 4.82772 15.823C4.76243 16.3067 4.98681 16.7611 5.25225 17.1405C5.32991 17.2616 5.42371 17.374 5.47268 17.5116C5.52594 17.6951 5.35035 17.8868 5.17064 17.866C5.06859 17.866 4.99918 17.7784 4.93389 17.716C4.48874 17.2572 4.03982 16.807 3.59467 16.3484C3.88863 16.0647 4.16231 15.7605 4.46023 15.4811ZM13.545 17.8328C14.3575 17.5658 15.2393 17.5367 16.0764 17.7118C17.4974 18.0204 18.7548 18.8709 19.7594 19.9256C20.7433 20.943 21.5273 22.219 21.7927 23.6405C21.9396 24.4202 21.907 25.2374 21.6867 26.0004C21.4702 26.7551 21.078 27.4514 20.5841 28.0518C20.3717 27.8225 20.1391 27.6098 19.9348 27.3763C19.9348 27.2637 20.0042 27.1677 20.0248 27.0595C20.135 26.6926 20.0248 26.2798 19.7511 26.0128C17.7343 23.9491 15.7091 21.8853 13.6919 19.8214C13.5082 19.6297 13.2756 19.4754 13.0102 19.4546C12.7937 19.4254 12.5813 19.5046 12.3772 19.5795C12.2954 19.6254 12.2425 19.5214 12.1894 19.4795C12.0261 19.3004 11.8463 19.1377 11.6831 18.9542C11.6831 18.9418 11.679 18.9167 11.679 18.9042C12.2221 18.4289 12.8591 18.0579 13.545 17.8328ZM19.049 17.6033C19.2368 17.6658 19.4082 17.7618 19.5838 17.8535C20.2413 18.1788 20.8943 18.5165 21.556 18.8458C21.613 18.8751 21.6661 18.9209 21.7152 18.9667C22.2703 19.5421 22.8299 20.1091 23.3891 20.6804C23.6936 21.0098 24.0679 21.2636 24.4834 21.4225C24.7732 21.5351 25.1 21.5351 25.3898 21.4307C25.5369 21.3807 25.6962 21.3225 25.8513 21.3725C25.9289 21.4051 25.9962 21.459 26.0459 21.5281C26.0956 21.5972 26.1256 21.679 26.1329 21.7644C26.1207 21.9937 25.9777 22.1854 25.8267 22.3439C25.3272 22.8609 24.8237 23.3738 24.316 23.8825C24.1813 24.0075 24.0262 24.1367 23.8382 24.1575C23.5689 24.1991 23.3034 23.9075 23.385 23.6405C23.4627 23.3695 23.5688 23.0944 23.524 22.8109C23.4586 22.3354 23.1931 21.9144 22.8992 21.5518C21.7927 20.4177 20.6822 19.2877 19.5716 18.1495C19.4 17.966 19.2122 17.7993 19.049 17.6033ZM31.502 18.7374C31.8085 18.4247 32.1186 18.1119 32.4249 17.7953C32.5596 17.9077 32.678 18.0412 32.8046 18.1663C32.7515 18.5832 32.8905 18.9958 33.1027 19.3461C33.2128 19.5463 33.3721 19.7172 33.4702 19.9256C33.5233 20.0258 33.4865 20.1382 33.4498 20.2382C33.965 20.5937 34.4144 21.0398 34.7768 21.5558C35.1891 22.1521 35.479 22.8609 35.4626 23.603C35.4668 24.3077 35.1687 24.983 34.7318 25.5167C34.6134 25.3916 34.4746 25.2791 34.3724 25.1374C34.393 24.983 34.4663 24.833 34.4296 24.6788C34.3889 24.4786 34.2377 24.3368 34.103 24.1991C33.0252 23.1026 31.9513 22.0063 30.8775 20.9097C30.7713 20.8012 30.6447 20.7012 30.4935 20.6888C30.3507 20.6719 30.2078 20.7137 30.0772 20.7595C29.9506 20.6388 29.8241 20.5093 29.7016 20.3842C30.2446 19.909 30.9508 19.6046 31.6736 19.6421C31.9513 19.6339 32.2208 19.7005 32.4902 19.763C32.1636 19.417 31.8164 19.0918 31.502 18.7374ZM10.1357 21.1847C10.3602 20.5468 10.7153 19.9632 11.1359 19.4419C11.3442 19.659 11.5648 19.8672 11.7689 20.0882C11.8096 20.2218 11.7036 20.3468 11.6831 20.4802C11.577 20.8388 11.7158 21.2307 11.9731 21.4891C13.9573 23.5197 15.9458 25.546 17.9343 27.5763C18.1262 27.7891 18.3588 27.9891 18.6528 28.0351C18.9141 28.0768 19.1715 27.9851 19.4123 27.8933C19.6328 28.1184 19.8534 28.3395 20.0738 28.5646C19.5919 28.994 19.0325 29.3318 18.4365 29.5695C17.5381 29.9237 16.5337 29.9821 15.5905 29.7697C14.1573 29.4484 12.8957 28.5688 11.8872 27.4972C10.9564 26.5009 10.2049 25.2791 9.93964 23.9156C9.7601 23.0111 9.81731 22.0521 10.1357 21.1847ZM28.693 22.3898C28.7421 21.7561 29.0156 21.1516 29.424 20.6761C29.5422 20.8012 29.669 20.9263 29.7913 21.0514C29.7465 21.2016 29.6769 21.36 29.718 21.5182C29.7424 21.6602 29.8362 21.7728 29.9343 21.8728C31.0122 22.9693 32.0818 24.07 33.1599 25.1665C33.2864 25.2916 33.4089 25.4418 33.5886 25.4918C33.752 25.5377 33.9193 25.4791 34.0784 25.4291C34.2011 25.5544 34.3277 25.6795 34.4461 25.8128C33.9315 26.2463 33.2905 26.5509 32.6127 26.5509C31.9554 26.58 31.3103 26.3423 30.7632 25.9921C30.367 25.7337 30.0037 25.4209 29.7016 25.0581C29.8444 25.546 29.8119 26.0923 29.5872 26.5509C29.3668 27.0011 29.0239 27.3681 28.6849 27.7225C28.5624 27.5974 28.4358 27.4679 28.3092 27.343C28.4155 27.1677 28.4849 26.9593 28.4195 26.7551C28.342 26.5465 28.1745 26.3965 28.0276 26.2381C27.7237 25.9243 27.4188 25.6116 27.1129 25.3C26.9333 25.129 26.7413 24.933 26.4841 24.9163C26.333 24.8998 26.1983 24.979 26.0717 25.054C25.9453 24.929 25.8228 24.7997 25.6962 24.6747C26.186 24.1702 26.7454 23.649 27.464 23.5572C28.052 23.457 28.644 23.7156 29.0605 24.1284C28.7991 23.5947 28.6359 22.9942 28.693 22.3898ZM7.17133 23.8449C7.29382 23.724 7.45309 23.6323 7.62455 23.6198C7.90631 23.603 8.16368 23.9032 8.08585 24.1826C8.02469 24.3953 7.87367 24.5828 7.86542 24.8123C7.88174 25.0123 8.04513 25.154 8.17588 25.2875L14.4636 31.7082C14.5617 31.8042 14.6719 31.9 14.8107 31.9125C15.0474 31.9125 15.2313 31.7333 15.4558 31.6791C15.7252 31.6249 15.9948 31.8751 15.9826 32.1502C15.9743 32.3379 15.8723 32.5088 15.7457 32.6421L12.5813 35.8735C12.4425 36.0111 12.271 36.1319 12.0711 36.1319C11.8261 36.1404 11.5974 35.9028 11.6177 35.6482C11.6424 35.3982 11.8463 35.1981 11.8506 34.9395C11.8424 34.7853 11.7238 34.6684 11.63 34.5602C10.6909 33.597 9.74773 32.6421 8.81276 31.6833C8.776 31.6542 8.72703 31.5707 8.67394 31.629C8.34734 31.95 8.03293 32.2879 7.70616 32.6088C7.48573 32.3837 7.2653 32.1586 7.04471 31.9293C7.3614 31.5913 7.6854 31.2604 8.01644 30.937C8.01644 30.9202 8.02074 30.8828 8.02074 30.866C7.13456 29.9781 6.26488 29.0774 5.383 28.1811C5.2483 28.0518 5.12151 27.8893 4.93389 27.8433C4.6767 27.8265 4.47655 28.0309 4.23567 28.0768C3.92539 28.1311 3.64363 27.7725 3.74981 27.4763C3.80289 27.2679 3.96611 27.1177 4.10905 26.9761C5.12975 25.9297 6.15458 24.8914 7.17133 23.8449ZM25.402 24.9707C25.892 25.4375 26.3535 25.9379 26.8352 26.4174C27.7499 27.3554 28.6686 28.2935 29.5872 29.2316C29.6525 29.286 29.7137 29.3609 29.7994 29.3775C29.9752 29.3944 30.1138 29.2191 30.2896 29.2525C30.3957 29.29 30.4935 29.3944 30.4815 29.5151C30.4815 29.6777 30.3466 29.7904 30.2485 29.8988C29.673 30.4784 29.1053 31.0661 28.5298 31.6458C28.4031 31.7875 28.1662 31.7167 28.0887 31.5583C27.9745 31.3623 28.2848 31.1537 28.1215 30.9746C26.9367 29.7689 25.7539 28.5612 24.5732 27.3512C24.4875 27.2721 24.4141 27.1721 24.3119 27.1177C24.1363 27.0677 23.9975 27.2179 23.8343 27.2388C23.7077 27.2263 23.5852 27.1137 23.5932 26.9802C23.581 26.8342 23.6872 26.7219 23.7769 26.626C24.3202 26.0754 24.8632 25.5211 25.402 24.9707ZM21.7601 28.6856C22.2458 28.1977 22.7194 27.6933 23.2136 27.2137C23.6505 27.664 24.0915 28.1102 24.5284 28.5604C24.6753 28.7063 24.5201 28.9274 24.3691 28.994C24.2629 29.0065 24.1648 28.9398 24.0709 28.8982C23.6955 28.7104 23.2625 28.6188 22.8502 28.7272C22.5889 28.7814 22.3602 28.9314 22.1315 29.0689C22.009 28.9398 21.8867 28.8107 21.7601 28.6856ZM19.7755 30.716C20.3392 30.1323 20.9108 29.5611 21.4742 28.9814C22.3887 29.903 23.291 30.8411 24.2099 31.7625C24.9122 32.4754 25.6063 33.1968 26.3126 33.9056C25.7656 34.4809 25.2022 35.0397 24.6548 35.6107C24.5774 35.6984 24.4671 35.7484 24.365 35.7942C24.2629 35.7568 24.1363 35.7193 24.1037 35.6026C24.0425 35.4065 24.2425 35.2523 24.2181 35.0605C24.1363 34.8979 23.9934 34.7811 23.8709 34.6519L20.8943 31.6123C20.7189 31.4456 20.5719 31.2456 20.3636 31.1205C20.1554 31.0786 19.9676 31.3497 19.7676 31.2037C19.5962 31.0912 19.6451 30.8411 19.7755 30.716ZM23.7362 29.6821C23.6709 29.5111 23.826 29.3109 24.0016 29.34C24.0832 29.3609 24.1444 29.4151 24.2016 29.4735C24.6303 29.9156 25.0632 30.3491 25.4879 30.7911C25.6266 30.937 25.8063 31.0621 25.8635 31.2705C25.8104 31.3623 25.7532 31.4874 25.6309 31.4998C25.4305 31.5207 25.2836 31.3581 25.1039 31.2912C24.9814 31.2291 24.8463 31.1972 24.7095 31.198C24.5727 31.1987 24.4379 31.2322 24.316 31.2956C24.1855 31.1621 24.0588 31.0286 23.9281 30.8995C24.018 30.7035 24.0588 30.4784 23.9934 30.2698C23.9444 30.0572 23.8056 29.8821 23.7362 29.6821ZM26.3616 30.7702C26.4473 30.6368 26.6555 30.616 26.7576 30.7411C27.1986 31.1956 27.6479 31.6458 28.0846 32.1044C27.5824 32.6047 27.1007 33.13 26.5861 33.6179C26.4679 33.4886 26.3411 33.3635 26.2186 33.2342C26.6555 32.684 26.7169 31.8875 26.4229 31.2539C26.3696 31.1037 26.2554 30.9202 26.3616 30.7702Z"
          fill="white"
        />
        <path
          d="M0.0913712 32.4296C0.254587 32.363 0.4099 32.2586 0.589609 32.2628C0.805913 32.3046 0.940781 32.5005 1.09163 32.6463C3.14557 34.7353 5.19109 36.8282 7.24074 38.917C7.35499 39.0465 7.50206 39.1756 7.5225 39.3591C7.51426 39.5382 7.42028 39.6968 7.34709 39.8553C4.92565 39.8511 2.50868 39.851 0.0913712 39.851C0.0913712 37.3786 0.0872478 34.9019 0.0913712 32.4296Z"
          fill="#F96302"
        />
        <path
          d="M19.0979 32.317C19.8247 32.2546 20.5475 32.5212 21.1476 32.9258C21.6171 33.2469 22.054 33.6179 22.3887 34.0807C22.789 34.6269 23.0338 35.294 23.0381 35.9819C23.0502 36.7658 22.7481 37.5579 22.2011 38.1123C22.0751 37.9881 21.9512 37.8616 21.8295 37.733C21.9028 37.5914 21.9886 37.4412 21.9642 37.2744C21.9235 37.0242 21.7559 36.8198 21.5884 36.649L18.8325 33.8346C18.6447 33.647 18.4324 33.4426 18.1547 33.4219C18.0037 33.4053 17.8688 33.4844 17.7424 33.5595C17.6238 33.426 17.4606 33.326 17.3871 33.1593C17.8362 32.6881 18.4487 32.3586 19.0979 32.317Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_1931_7633">
          <rect fill="white" height="40" width="39" />
        </clipPath>
      </defs>
    </svg>
  ),
  JukeboxSecondaryMark: ({ color = '#0C4D32', size="32", ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      viewBox="0 0 32 32"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <g clipPath="url(#clip0_518_3099)">
        <path d="M16 0V16C24.8364 16 32 8.8364 32 0H16Z" fill={color} />
        <path d="M0 32C8.8364 32 16 24.8364 16 16H0V32Z" fill={color} />
        <path
          d="M24.5 32C28.6421 32 32 28.6421 32 24.5C32 20.3579 28.6421 17 24.5 17C20.3579 17 17 20.3579 17 24.5C17 28.6421 20.3579 32 24.5 32Z"
          fill={color}
        />
      </g>
      <defs>
        <clipPath id="clip0_518_3099">
          <rect fill="white" height="32" width="32" />
        </clipPath>
      </defs>
    </svg>
  ),
  JukeboxLogo: ({ color = '#0C4D32', ...rest }: IconProps) => (
    <svg
      fill="none"
      height={18}
      viewBox="0 0 192 18"
      width={192}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path
        d="M25.1827 12.1015C25.1827 14.5447 24.0896 15.5752 22.4461 15.5752C20.6963 15.5752 20.0013 14.4548 20.0013 12.5137V5.35059H16.5811V13.1847C16.5811 16.1312 18.2042 17.8087 21.2262 17.8087C23.241 17.8087 24.6259 16.8741 25.2621 15.4853V17.5223H28.6016V5.35059H25.1814V12.1015H25.1827Z"
        fill={color}
      />
      <path
        d="M82.5882 15.6705C80.3878 15.6705 79.168 13.999 79.168 11.436C79.168 8.89688 80.3878 7.20136 82.5882 7.20136C84.7616 7.20136 86.0083 8.89688 86.0083 11.436C86.007 13.999 84.7616 15.6705 82.5882 15.6705ZM82.5882 5.0625C78.6125 5.0625 75.7747 7.50572 75.7747 11.436C75.7747 15.389 78.6112 17.8082 82.5882 17.8082C86.5638 17.8082 89.4003 15.389 89.4003 11.436C89.4003 7.50572 86.5638 5.0625 82.5882 5.0625Z"
        fill={color}
      />
      <path
        d="M103.389 5.35059H99.8656L96.9587 9.11187L94.0775 5.35059H90.2963L95.0042 11.2915L89.9879 17.5211H93.5117L96.7783 13.3525L100.019 17.5211H103.8L98.7341 11.1489L103.389 5.35059Z"
        fill={color}
      />
      <path
        d="M124.467 0V7.60165H115.396V0H111.871V17.5207H115.396V10.094H124.467V17.5207H127.967V0H124.467Z"
        fill={color}
      />
      <path
        d="M67.0311 15.6946C64.8832 15.6946 63.6378 14.1608 63.6378 11.7895V11.0825C63.6378 8.71122 64.8832 7.17747 67.0311 7.17747C69.2314 7.17747 70.5037 8.94488 70.5037 11.436C70.5037 13.9511 69.2314 15.6946 67.0311 15.6946ZM67.9859 5.06258C65.8919 5.06258 64.3546 6.11703 63.7184 7.33804V0H60.3251V17.5207H63.6647V15.5796C64.3277 16.8018 65.9443 17.8071 67.9859 17.8071C71.5123 17.8071 73.9239 15.2201 73.9239 11.4348C73.9239 7.67355 71.5111 5.06258 67.9859 5.06258Z"
        fill={color}
      />
      <path
        d="M173.163 2.44531H169.797V5.48765H167.095V7.69242H169.797V9.2705V12.3895C169.797 15.8608 171.703 17.5887 174.984 17.5887H176.759V14.8687H175.072C173.27 14.8687 173.13 13.6201 173.13 11.9785L173.138 7.69242H176.826V5.48765H173.165V2.44531H173.163Z"
        fill={color}
      />
      <path
        d="M164.26 12.5744V0H160.865V12.3898C160.865 15.1578 162.097 16.8161 164.26 17.3757C164.809 17.5183 165.419 17.589 166.084 17.589H167.707V14.869H166.172C164.594 14.869 164.26 13.9128 164.26 12.5744Z"
        fill={color}
      />
      <path
        d="M187.069 5.06258C185.028 5.06258 183.543 5.9972 182.881 7.40993V0H179.488V17.5207H182.881V10.7698C182.881 8.32659 184.207 7.34403 185.903 7.34403C187.68 7.34403 188.607 8.48835 188.607 10.4283V17.5195H192V9.71056C192 6.78685 190.144 5.06258 187.069 5.06258Z"
        fill={color}
      />
      <path
        d="M48.3558 10.1898C48.7194 8.32052 49.8893 7.33916 51.6301 7.33916C53.6307 7.33916 54.5664 8.68119 54.5664 10.1898H48.3558ZM51.6301 5.0625C47.9142 5.0625 45.0304 7.48175 45.0304 11.4587C45.0304 15.4357 47.863 17.8082 51.7082 17.8082C54.3373 17.8082 56.471 16.6962 57.353 14.6796L54.592 13.7354C54.1504 14.9097 53.2147 15.5567 51.7082 15.5567C49.6563 15.5567 48.4608 14.3345 48.2522 12.1789H57.7357V11.1963C57.7344 7.96105 55.7606 5.0625 51.6301 5.0625Z"
        fill={color}
      />
      <path
        d="M134.28 10.1898C134.639 8.32052 135.798 7.33916 137.521 7.33916C139.501 7.33916 140.428 8.68119 140.428 10.1898H134.28ZM137.521 5.0625C133.842 5.0625 130.988 7.48175 130.988 11.4587C130.988 15.4357 133.791 17.8082 137.599 17.8082C140.201 17.8082 142.313 16.6962 143.187 14.6796L140.454 13.7354C140.017 14.9097 139.091 15.5567 137.6 15.5567C135.567 15.5567 134.385 14.3345 134.179 12.1789H143.567V11.1963C143.565 7.96105 141.61 5.0625 137.521 5.0625Z"
        fill={color}
      />
      <path
        d="M151.059 15.6514C149.708 15.6514 148.954 14.9145 148.934 13.9559C148.913 12.8127 149.786 12.4664 151.002 12.2855C151.227 12.252 151.453 12.2292 151.683 12.21C152.299 12.1597 153.829 12.0171 154.406 11.7954L154.423 12.7996C154.461 14.7179 152.809 15.6514 151.059 15.6514ZM157.682 9.78238C157.62 6.62022 155.497 5.0625 151.839 5.0625C148.805 5.0625 146.685 6.34821 146.164 8.56976L149.321 9.20722C149.405 8.08087 150.292 7.33796 151.776 7.33796C153.102 7.33796 154.044 7.87238 154.296 8.97237C154.298 8.97955 154.3 8.98794 154.301 8.99873C154.404 9.60863 153.916 10.1634 153.24 10.2317L150.353 10.5241C147.628 10.8117 145.526 11.8182 145.573 14.2614C145.615 16.4662 147.549 17.783 150.175 17.783C152.323 17.783 153.985 16.5584 154.605 15.0331C154.616 15.6322 154.691 16.8484 154.835 17.5194H158.095C157.864 16.5369 157.793 15.6022 157.771 14.4771L157.682 9.78238Z"
        fill={color}
      />
      <path
        d="M39.6377 5.35015L35.52 10.3097V0.216882V0H32.1267V17.5207H35.52V12.3155H36.5043C36.9254 12.3155 37.3465 12.3719 37.7408 12.5097C39.2013 13.0189 40.0768 14.3753 40.1869 16.2566L40.2611 17.5207H43.4701C43.4701 17.5207 43.4854 16.1823 43.2051 14.7623C42.7853 12.6271 41.2723 11.0142 39.2973 10.2594L43.5712 5.34896H39.6377V5.35015Z"
        fill={color}
      />
      <path
        d="M9.70496 11.7991C9.70496 13.8085 8.56832 15.2944 6.51136 15.2944C4.50688 15.2944 3.36896 13.6696 3.36896 11.6613V9.49967H0V11.5774C0 15.6479 2.4256 18.0012 6.53952 18.0012C10.6803 18.0012 13.2019 15.6227 13.2019 11.5774V0H9.70496V11.7991Z"
        fill={color}
      />
    </svg>
  ),

  ManyDots: () => (
    <svg
      fill="none"
      height="612"
      viewBox="0 0 181 612"
      width="181"
      xmlns="http://www.w3.org/2000/svg"
    >
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="0.5"
        x2="0.499976"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="6.50006"
        x2="6.50004"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="12.5001"
        x2="12.5"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="18.5001"
        x2="18.5001"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="24.5001"
        x2="24.5001"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="30.5001"
        x2="30.5001"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="36.5002"
        x2="36.5002"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="42.5002"
        x2="42.5002"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="48.5002"
        x2="48.5002"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="54.5002"
        x2="54.5002"
        y1="1.62072e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="60.5003"
        x2="60.5003"
        y1="1.62072e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="66.5003"
        x2="66.5003"
        y1="1.62072e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="72.5004"
        x2="72.5003"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="78.5004"
        x2="78.5003"
        y1="1.62072e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="84.5004"
        x2="84.5004"
        y1="1.62072e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="90.5005"
        x2="90.5005"
        y1="1.62072e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="96.5005"
        x2="96.5005"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="102.5"
        x2="102.5"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="108.501"
        x2="108.501"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="114.501"
        x2="114.501"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="120.501"
        x2="120.501"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="126.501"
        x2="126.501"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="132.501"
        x2="132.501"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="138.501"
        x2="138.501"
        y1="2.43108e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="144.501"
        x2="144.501"
        y1="1.45856e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="150.501"
        x2="150.501"
        y1="1.45856e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="156.501"
        x2="156.501"
        y1="1.45856e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="162.501"
        x2="162.501"
        y1="1.45856e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="168.501"
        x2="168.501"
        y1="1.45856e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="174.501"
        x2="174.501"
        y1="1.45856e-08"
        y2="612"
      />
      <line
        stroke="#128632"
        strokeDasharray="1 6"
        x1="180.501"
        x2="180.501"
        y1="2.43108e-08"
        y2="612"
      />
    </svg>
  ),
  NavigationBlank: ({ color = '#0C4D32', size = 16, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.75"
      viewBox="0 0 16 16"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="8" cy="8" r="7.125" />
    </svg>
  ),

  NavigationCheck: ({ color = '#0C4D32', size = 16, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.7"
      viewBox="0 0 16 16"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="8" cy="8" fill={color} r="8" />
      <path d="M11.3333 5.5L6.75 10.0833L4.66667 8" stroke="white" />
    </svg>
  ),

  DOC: ({ size = 47, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={Number(size) * 1.191}
      viewBox="0 0 47 56"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect fill="#3A5CA5" height="54" rx="4" width="45" y="2" />
      <path
        d="M11.3434 25.004C14.3534 25.004 16.3414 27.006 16.3414 30.002C16.3414 33.012 14.3534 35 11.3434 35H7.49338V25.004H11.3434ZM11.3434 26.474H9.31338V33.53H11.3434C13.3314 33.53 14.4374 32.228 14.4374 30.002C14.4374 27.776 13.3314 26.474 11.3434 26.474ZM27.1184 30.002C27.1184 33.012 25.2844 35.168 22.3444 35.168C19.4044 35.168 17.5704 33.012 17.5704 30.002C17.5704 27.006 19.4044 24.836 22.3444 24.836C25.2844 24.836 27.1184 27.006 27.1184 30.002ZM25.2284 30.002C25.2284 27.79 24.2484 26.306 22.3444 26.306C20.4264 26.306 19.4604 27.79 19.4604 30.002C19.4604 32.228 20.4264 33.698 22.3444 33.698C24.2484 33.698 25.2284 32.228 25.2284 30.002ZM30.2339 29.988C30.2339 32.27 31.2419 33.698 33.0479 33.698C34.4759 33.698 35.1339 32.886 35.3299 31.864H37.1359C36.7859 33.81 35.4419 35.168 33.0479 35.168C30.2479 35.168 28.3439 33.096 28.3439 29.988C28.3439 26.908 30.2479 24.836 33.0479 24.836C35.4419 24.836 36.7859 26.18 37.1359 28.14H35.3299C35.1339 27.118 34.4759 26.306 33.0479 26.306C31.2419 26.306 30.2339 27.72 30.2339 29.988Z"
        fill="white"
      />
      <g filter="url(#filter0_d_743_5261)" opacity="0.6">
        <path d="M29 2L37 10L45 18H32C30.3431 18 29 16.6569 29 15V2Z" fill="white" />
      </g>
      <path d="M45 18L37 10L29 2L42 2C43.6569 2 45 3.34315 45 5L45 18Z" fill="white" />
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="22"
          id="filter0_d_743_5261"
          width="22"
          x="25"
          y="0"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset dx="-1" dy="1" />
          <feGaussianBlur stdDeviation="1.5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.2 0 0 0 0 0.2 0 0 0 0 0.2 0 0 0 0.33 0" />
          <feBlend in2="BackgroundImageFix" mode="normal" result="effect1_dropShadow_743_5261" />
          <feBlend
            in="SourceGraphic"
            in2="effect1_dropShadow_743_5261"
            mode="normal"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  ),

  PDF: ({ size = 47, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={Number(size) * 1.191}
      viewBox="0 0 47 56"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect fill="#FA7921" height="54" rx="4" width="45" y="2" />
      <path
        d="M13.3678 31.318H11.0438V35H9.22384V25.004H13.3538C15.7198 25.004 17.0918 26.082 17.0918 28.168C17.0918 30.254 15.7198 31.318 13.3678 31.318ZM11.0438 29.862H13.1998C14.5718 29.862 15.2578 29.302 15.2578 28.168C15.2578 27.02 14.5718 26.474 13.1998 26.474H11.0438V29.862ZM22.5211 25.004C25.5311 25.004 27.5191 27.006 27.5191 30.002C27.5191 33.012 25.5311 35 22.5211 35H18.6711V25.004H22.5211ZM22.5211 26.474H20.4911V33.53H22.5211C24.5091 33.53 25.6151 32.228 25.6151 30.002C25.6151 27.776 24.5091 26.474 22.5211 26.474ZM29.2258 35V25.004H36.1278V26.474H31.0878V29.526H35.6098V30.996H31.0878V35H29.2258Z"
        fill="white"
      />
      <g filter="url(#filter0_d_743_5256)" opacity="0.6">
        <path d="M29 2L37 10L45 18H32C30.3431 18 29 16.6569 29 15V2Z" fill="white" />
      </g>
      <path d="M45 18L37 10L29 2L42 2C43.6569 2 45 3.34315 45 5L45 18Z" fill="white" />
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="22"
          id="filter0_d_743_5256"
          width="22"
          x="25"
          y="0"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset dx="-1" dy="1" />
          <feGaussianBlur stdDeviation="1.5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.2 0 0 0 0 0.2 0 0 0 0 0.2 0 0 0 0.33 0" />
          <feBlend in2="BackgroundImageFix" mode="normal" result="effect1_dropShadow_743_5256" />
          <feBlend
            in="SourceGraphic"
            in2="effect1_dropShadow_743_5256"
            mode="normal"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  ),

  CSV: ({ size = 47, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={Number(size) * 1.191}
      viewBox="0 0 47 56"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect fill="#128632" height="54" rx="4" width="45" y="2" />
      <path
        d="M9.80124 29.988C9.80124 32.27 10.8092 33.698 12.6152 33.698C14.0432 33.698 14.7012 32.886 14.8972 31.864H16.7032C16.3532 33.81 15.0092 35.168 12.6152 35.168C9.81524 35.168 7.91124 33.096 7.91124 29.988C7.91124 26.908 9.81524 24.836 12.6152 24.836C15.0092 24.836 16.3532 26.18 16.7032 28.14H14.8972C14.7012 27.118 14.0432 26.306 12.6152 26.306C10.8092 26.306 9.80124 27.72 9.80124 29.988ZM17.684 31.976H19.476C19.602 33.334 20.778 33.796 22.024 33.796C23.214 33.796 24.138 33.264 24.138 32.256C24.138 31.528 23.704 31.122 22.5 30.898L20.666 30.562C19.196 30.296 18.006 29.596 18.006 27.846C18.006 26.068 19.574 24.836 21.758 24.836C24.138 24.836 25.566 25.872 25.762 27.748H24.012C23.886 26.768 23.018 26.222 21.772 26.222C20.568 26.222 19.812 26.852 19.812 27.734C19.812 28.56 20.33 28.826 21.408 29.036L23.34 29.414C24.978 29.722 25.958 30.534 25.958 32.144C25.958 33.964 24.292 35.168 22.08 35.168C19.7 35.168 17.866 34.174 17.684 31.976ZM30.4702 35L26.5222 25.004H28.4962L31.5342 33.012H31.5622L34.6142 25.004H36.5042L32.5422 35H30.4702Z"
        fill="white"
      />
      <g filter="url(#filter0_d_743_5267)" opacity="0.6">
        <path d="M29 2L37 10L45 18H32C30.3431 18 29 16.6569 29 15V2Z" fill="white" />
      </g>
      <path d="M45 18L37 10L29 2L42 2C43.6569 2 45 3.34315 45 5L45 18Z" fill="white" />
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="22"
          id="filter0_d_743_5267"
          width="22"
          x="25"
          y="0"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset dx="-1" dy="1" />
          <feGaussianBlur stdDeviation="1.5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.2 0 0 0 0 0.2 0 0 0 0 0.2 0 0 0 0.33 0" />
          <feBlend in2="BackgroundImageFix" mode="normal" result="effect1_dropShadow_743_5267" />
          <feBlend
            in="SourceGraphic"
            in2="effect1_dropShadow_743_5267"
            mode="normal"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  ),

  CheckedCheckbox: ({ color = 'currentColor', size = 16, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 16 16"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M11.5 6L6.6875 10L4.5 8.18182" />
    </svg>
  ),

  // The following are Lucide (not feather) icons. Adding here for now in case
  // we decide to revisit we know where they came from. https://lucide.dev
  ChevronsUpDown: ({ color = 'currentColor', size = 16, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="m7 15 5 5 5-5" />
      <path d="m7 9 5-5 5 5" />
    </svg>
  ),
  GripVertical: ({ color = 'currentColor', size = 16, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="9" cy="12" r="1" />
      <circle cx="9" cy="5" r="1" />
      <circle cx="9" cy="19" r="1" />
      <circle cx="15" cy="12" r="1" />
      <circle cx="15" cy="5" r="1" />
      <circle cx="15" cy="19" r="1" />
    </svg>
  ),

  PanelLeft: ({ color = 'currentColor', size = 16, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect height="18" rx="2" width="18" x="3" y="3" />
      <path d="M9 3v18" />
    </svg>
  ),

  ConstructionPm: ({ color = 'currentColor', size = 16, ...rest }: IconProps) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M1.66667 10V36.6667L13.3333 30L26.6667 36.6667L38.3333 30V3.33333L26.6667 10L13.3333 3.33333L1.66667 10Z" stroke="#4D4D4D" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M13.3333 3.33333V30" stroke="#4D4D4D" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M26.6667 10V36.6667" stroke="#4D4D4D" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  ),

  Installer: ({ color = 'currentColor', size = 16, ...rest }: IconProps) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M24.5 10.5C24.1946 10.8115 24.0236 11.2304 24.0236 11.6667C24.0236 12.1029 24.1946 12.5218 24.5 12.8333L27.1667 15.5C27.4782 15.8054 27.8971 15.9764 28.3333 15.9764C28.7696 15.9764 29.1885 15.8054 29.5 15.5L35.7833 9.21667C36.6214 11.0686 36.8751 13.1321 36.5108 15.1319C36.1464 17.1318 35.1812 18.9731 33.7438 20.4105C32.3064 21.8479 30.4651 22.8131 28.4652 23.1774C26.4654 23.5418 24.402 23.2881 22.55 22.45L11.0333 33.9667C10.3703 34.6297 9.47102 35.0022 8.53334 35.0022C7.59565 35.0022 6.69638 34.6297 6.03334 33.9667C5.3703 33.3036 4.9978 32.4043 4.9978 31.4667C4.9978 30.529 5.3703 29.6297 6.03334 28.9667L17.55 17.45C16.7119 15.598 16.4582 13.5346 16.8226 11.5348C17.187 9.53491 18.1521 7.69359 19.5895 6.2562C21.0269 4.81881 22.8682 3.85361 24.8681 3.48924C26.8679 3.12486 28.9314 3.37861 30.7833 4.21667L24.5167 10.4833L24.5 10.5Z" stroke="#4D4D4D" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  ),

  OT: ({ color = 'currentColor', size = 16, ...rest }: IconProps) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M5 15L20 3.33333L35 15V33.3333C35 34.2174 34.6488 35.0652 34.0237 35.6904C33.3986 36.3155 32.5507 36.6667 31.6667 36.6667H8.33333C7.44928 36.6667 6.60143 36.3155 5.97631 35.6904C5.35119 35.0652 5 34.2174 5 33.3333V15Z" stroke="#4D4D4D" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M19.2222 28.4444C22.6587 28.4444 25.4444 25.6587 25.4444 22.2222C25.4444 18.7858 22.6587 16 19.2222 16C15.7858 16 13 18.7858 13 22.2222C13 25.6587 15.7858 28.4444 19.2222 28.4444Z" stroke="#4D4D4D" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M27 30L23.6167 26.6167" stroke="#4D4D4D" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  ),

  CareCoordinator: ({ color = 'currentColor', size = 16, ...rest }: IconProps) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M26.6667 6.66667H30C30.8841 6.66667 31.7319 7.01786 32.357 7.64298C32.9821 8.2681 33.3333 9.11594 33.3333 10V33.3333C33.3333 34.2174 32.9821 35.0652 32.357 35.6904C31.7319 36.3155 30.8841 36.6667 30 36.6667H10C9.11594 36.6667 8.2681 36.3155 7.64297 35.6904C7.01785 35.0652 6.66666 34.2174 6.66666 33.3333V10C6.66666 9.11594 7.01785 8.2681 7.64297 7.64298C8.2681 7.01786 9.11594 6.66667 10 6.66667H13.3333" stroke="#4D4D4D" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M25 3.33333H15C14.0795 3.33333 13.3333 4.07953 13.3333 5V8.33333C13.3333 9.25381 14.0795 10 15 10H25C25.9205 10 26.6667 9.25381 26.6667 8.33333V5C26.6667 4.07953 25.9205 3.33333 25 3.33333Z" stroke="#4D4D4D" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M27 22.3567V23.0007C26.9991 24.5102 26.5103 25.9789 25.6065 27.1879C24.7027 28.3969 23.4323 29.2814 21.9847 29.7094C20.5372 30.1374 18.9901 30.086 17.5741 29.5629C16.1582 29.0398 14.9493 28.0729 14.1277 26.8066C13.3061 25.5403 12.9159 24.0423 13.0152 22.5361C13.1145 21.0299 13.698 19.5961 14.6788 18.4486C15.6595 17.3011 16.9849 16.5014 18.4573 16.1688C19.9297 15.8361 21.4702 15.9883 22.849 16.6027" stroke="#4D4D4D" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M27 17.4007L20 24.4077L17.9 22.3077" stroke="#4D4D4D" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  ),

  LongArrowRight: ({ color = 'currentColor', ...rest }: IconProps) => (
    <svg
      fill="none"
      height="16"
      viewBox="0 0 21 16"
      width="21"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M20.7071 8.70711C21.0976 8.31658 21.0976 7.68342 20.7071 7.2929L14.3431 0.928933C13.9526 0.538409 13.3195 0.538409 12.9289 0.928933C12.5384 1.31946 12.5384 1.95262 12.9289 2.34315L18.5858 8L12.9289 13.6569C12.5384 14.0474 12.5384 14.6805 12.9289 15.0711C13.3195 15.4616 13.9526 15.4616 14.3431 15.0711L20.7071 8.70711ZM0 8L-8.74228e-08 9L20 9L20 8L20 7L8.74228e-08 7L0 8Z" fill={color} />
    </svg>
  ),

  // Feather Icons

  Activity: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  AlertCircle: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </svg>
  ),
  AlertTriangle: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" x2="12" y1="9" y2="13" />
      <line x1="12" x2="12.01" y1="17" y2="17" />
    </svg>
  ),
  AlignCenter: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="18" x2="6" y1="10" y2="10" />
      <line x1="21" x2="3" y1="6" y2="6" />
      <line x1="21" x2="3" y1="14" y2="14" />
      <line x1="18" x2="6" y1="18" y2="18" />
    </svg>
  ),
  AlignJustify: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="21" x2="3" y1="10" y2="10" />
      <line x1="21" x2="3" y1="6" y2="6" />
      <line x1="21" x2="3" y1="14" y2="14" />
      <line x1="21" x2="3" y1="18" y2="18" />
    </svg>
  ),
  AlignLeft: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="17" x2="3" y1="10" y2="10" />
      <line x1="21" x2="3" y1="6" y2="6" />
      <line x1="21" x2="3" y1="14" y2="14" />
      <line x1="17" x2="3" y1="18" y2="18" />
    </svg>
  ),
  AlignRight: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="21" x2="7" y1="10" y2="10" />
      <line x1="21" x2="3" y1="6" y2="6" />
      <line x1="21" x2="3" y1="14" y2="14" />
      <line x1="21" x2="7" y1="18" y2="18" />
    </svg>
  ),
  Archive: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="21 8 21 21 3 21 3 8" />
      <rect height="5" width="22" x="1" y="3" />
      <line x1="10" x2="14" y1="12" y2="12" />
    </svg>
  ),
  ArrowDownCircle: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="8 12 12 16 16 12" />
      <line x1="12" x2="12" y1="8" y2="16" />
    </svg>
  ),
  ArrowDown: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="12" x2="12" y1="5" y2="19" />
      <polyline points="19 12 12 19 5 12" />
    </svg>
  ),
  ArrowLeftCircle: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 8 8 12 12 16" />
      <line x1="16" x2="8" y1="12" y2="12" />
    </svg>
  ),
  ArrowLeft: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="19" x2="5" y1="12" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  ),
  ArrowRightCircle: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 16 16 12 12 8" />
      <line x1="8" x2="16" y1="12" y2="12" />
    </svg>
  ),
  ArrowRight: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="5" x2="19" y1="12" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  ArrowUpCircle: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="16 12 12 8 8 12" />
      <line x1="12" x2="12" y1="16" y2="8" />
    </svg>
  ),
  ArrowUp: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="12" x2="12" y1="19" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  ),
  Award: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="8" r="7"/>
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/>
    </svg>
  ),
  BarChart2: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="18" x2="18" y1="20" y2="10" />
      <line x1="12" x2="12" y1="20" y2="4" />
      <line x1="6" x2="6" y1="20" y2="14" />
    </svg>
  ),
  BarChart: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="12" x2="12" y1="20" y2="10" />
      <line x1="18" x2="18" y1="20" y2="4" />
      <line x1="6" x2="6" y1="20" y2="16" />
    </svg>
  ),
  BellOff: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      <path d="M18.63 13A17.89 17.89 0 0 1 18 8" />
      <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" />
      <path d="M18 8a6 6 0 0 0-9.33-5" />
      <line x1="1" x2="23" y1="1" y2="23" />
    </svg>
  ),
  Bell: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  ),
  Bold: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
      <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
    </svg>
  ),
  BookOpen: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  ),
  BookMark: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Calendar: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect height="18" rx="2" ry="2" width="18" x="3" y="4" />
      <line x1="16" x2="16" y1="2" y2="6" />
      <line x1="8" x2="8" y1="2" y2="6" />
      <line x1="3" x2="21" y1="10" y2="10" />
    </svg>
  ),
  CameraOff: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="1" x2="23" y1="1" y2="23" />
      <path d="M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3m3-3h6l2 3h4a2 2 0 0 1 2 2v9.34m-7.72-2.06a4 4 0 1 1-5.56-5.56" />
    </svg>
  ),
  Camera: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  ),
  CheckCircle: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  CheckSquare: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="9 11 12 14 22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  Check: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  ChevronDown: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  ChevronLeft: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  ChevronRight: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  ChevronUp: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="18 15 12 9 6 15" />
    </svg>
  ),
  ChevronsDown: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="7 13 12 18 17 13" />
      <polyline points="7 6 12 11 17 6" />
    </svg>
  ),
  ChevronsLeft: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="11 17 6 12 11 7" />
      <polyline points="18 17 13 12 18 7" />
    </svg>
  ),
  ChevronsRight: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="13 17 18 12 13 7" />
      <polyline points="6 17 11 12 6 7" />
    </svg>
  ),
  ChevronsUp: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="17 11 12 6 7 11" />
      <polyline points="17 18 12 13 7 18" />
    </svg>
  ),
  Circle: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
    </svg>
  ),
  Clipboard: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect height="4" rx="1" ry="1" width="8" x="8" y="2" />
    </svg>
  ),
  Clock: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  CloudOff: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M22.61 16.95A5 5 0 0 0 18 10h-1.26a8 8 0 0 0-7.05-6M5 5a8 8 0 0 0 4 15h9a5 5 0 0 0 1.7-.3" />
      <line x1="1" x2="23" y1="1" y2="23" />
    </svg>
  ),
  Cloud: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
    </svg>
  ),
  Command: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M18 3a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3H6a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3V6a3 3 0 0 0-3-3 3 3 0 0 0-3 3 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 3 3 0 0 0-3-3z" />
    </svg>
  ),
  Copy: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect height="13" rx="2" ry="2" width="13" x="9" y="9" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  ),
  CreditCard: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect height="16" rx="2" ry="2" width="22" x="1" y="4" />
      <line x1="1" x2="23" y1="10" y2="10" />
    </svg>
  ),
  Crop: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15" />
      <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15" />
    </svg>
  ),
  Delete: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
      <line x1="18" x2="12" y1="9" y2="15" />
      <line x1="12" x2="18" y1="9" y2="15" />
    </svg>
  ),
  DollarSign: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="12" x2="12" y1="1" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  DownloadCloud: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="8 17 12 21 16 17" />
      <line x1="12" x2="12" y1="12" y2="21" />
      <path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.29" />
    </svg>
  ),
  Download: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" x2="12" y1="15" y2="3" />
    </svg>
  ),
  Edit2: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </svg>
  ),
  Edit3: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  ),
  Edit: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  ExternalLink: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" x2="21" y1="14" y2="3" />
    </svg>
  ),
  EyeOff: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" x2="23" y1="1" y2="23" />
    </svg>
  ),
  Eye: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  FileMinus: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" x2="15" y1="15" y2="15" />
    </svg>
  ),
  FilePlus: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="12" x2="12" y1="18" y2="12" />
      <line x1="9" x2="15" y1="15" y2="15" />
    </svg>
  ),
  FileText: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" x2="8" y1="13" y2="13" />
      <line x1="16" x2="8" y1="17" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  ),
  File: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <polyline points="13 2 13 9 20 9" />
    </svg>
  ),
  Filter: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  ),
  Flag: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" x2="4" y1="22" y2="15" />
    </svg>
  ),
  FolderMinus: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
      <line x1="9" x2="15" y1="14" y2="14" />
    </svg>
  ),
  FolderPlus: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
      <line x1="12" x2="12" y1="11" y2="17" />
      <line x1="9" x2="15" y1="14" y2="14" />
    </svg>
  ),
  Folder: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Globe: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="2" x2="22" y1="12" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  Grid: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect height="7" width="7" x="3" y="3" />
      <rect height="7" width="7" x="14" y="3" />
      <rect height="7" width="7" x="14" y="14" />
      <rect height="7" width="7" x="3" y="14" />
    </svg>
  ),
  Heart: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),
  HelpCircle: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" x2="12.01" y1="17" y2="17" />
    </svg>
  ),
  Home: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  Image: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect height="18" rx="2" ry="2" width="18" x="3" y="3" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  ),
  Inbox: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  ),
  Info: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="16" y2="12" />
      <line x1="12" x2="12.01" y1="8" y2="8" />
    </svg>
  ),
  Italic: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="19" x2="10" y1="4" y2="4" />
      <line x1="14" x2="5" y1="20" y2="20" />
      <line x1="15" x2="9" y1="4" y2="20" />
    </svg>
  ),
  Key: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  ),
  Layers: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  ),
  Layout: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect height="18" rx="2" ry="2" width="18" x="3" y="3" />
      <line x1="3" x2="21" y1="9" y2="9" />
      <line x1="9" x2="9" y1="21" y2="9" />
    </svg>
  ),
  Link2: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M15 7h3a5 5 0 0 1 5 5 5 5 0 0 1-5 5h-3m-6 0H6a5 5 0 0 1-5-5 5 5 0 0 1 5-5h3" />
      <line x1="8" x2="16" y1="12" y2="12" />
    </svg>
  ),
  Link: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  List: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="8" x2="21" y1="6" y2="6" />
      <line x1="8" x2="21" y1="12" y2="12" />
      <line x1="8" x2="21" y1="18" y2="18" />
      <line x1="3" x2="3.01" y1="6" y2="6" />
      <line x1="3" x2="3.01" y1="12" y2="12" />
      <line x1="3" x2="3.01" y1="18" y2="18" />
    </svg>
  ),
  Loader: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="12" x2="12" y1="2" y2="6" />
      <line x1="12" x2="12" y1="18" y2="22" />
      <line x1="4.93" x2="7.76" y1="4.93" y2="7.76" />
      <line x1="16.24" x2="19.07" y1="16.24" y2="19.07" />
      <line x1="2" x2="6" y1="12" y2="12" />
      <line x1="18" x2="22" y1="12" y2="12" />
      <line x1="4.93" x2="7.76" y1="19.07" y2="16.24" />
      <line x1="16.24" x2="19.07" y1="7.76" y2="4.93" />
    </svg>
  ),
  Lock: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect height="11" rx="2" ry="2" width="18" x="3" y="11" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  LogIn: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <polyline points="10 17 15 12 10 7" />
      <line x1="15" x2="3" y1="12" y2="12" />
    </svg>
  ),
  LogOut: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" x2="9" y1="12" y2="12" />
    </svg>
  ),
  Mail: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  MapPin: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  Map: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
      <line x1="8" x2="8" y1="2" y2="18" />
      <line x1="16" x2="16" y1="6" y2="22" />
    </svg>
  ),
  Maximize2: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" x2="14" y1="3" y2="10" />
      <line x1="3" x2="10" y1="21" y2="14" />
    </svg>
  ),
  Maximize: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
    </svg>
  ),
  Menu: ({ color = 'currentColor', size = 24, ...rest }: IconProps) =>(
    <svg
       fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="3" x2="21" y1="12" y2="12"/>
      <line x1="3" x2="21" y1="6" y2="6"/>
      <line x1="3" x2="21" y1="18" y2="18"/>
    </svg>
  ),
  MinusCircle: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="8" x2="16" y1="12" y2="12" />
    </svg>
  ),
  MinusSquare: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect height="18" rx="2" ry="2" width="18" x="3" y="3" />
      <line x1="8" x2="16" y1="12" y2="12" />
    </svg>
  ),
  Minus: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="5" x2="19" y1="12" y2="12" />
    </svg>
  ),
  Moon: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  ),
  Monitor: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect height="14" rx="2" ry="2" width="20" x="2" y="3" />
      <line x1="8" x2="16" y1="21" y2="21" />
      <line x1="12" x2="12" y1="17" y2="21" />
    </svg>
  ),
  MoreHorizontal: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="12" r="1" />
    </svg>
  ),
  MoreVertical: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="19" r="1" />
    </svg>
  ),
  MousePointer: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
      <path d="M13 13l6 6" />
    </svg>
  ),
  Move: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="5 9 2 12 5 15" />
      <polyline points="9 5 12 2 15 5" />
      <polyline points="15 19 12 22 9 19" />
      <polyline points="19 9 22 12 19 15" />
      <line x1="2" x2="22" y1="12" y2="12" />
      <line x1="12" x2="12" y1="2" y2="22" />
    </svg>
  ),
  Navigation: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polygon points="3 11 22 2 13 21 11 13 3 11" />
    </svg>
  ),
  Object: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 16 16"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M15.6246 5.25723C12.529 5.93509 11.7033 6.75952 11.0245 9.84997C10.9686 10.1043 10.6056 10.1043 10.5499 9.84997C9.87099 6.75934 9.04524 5.93492 5.94981 5.25723C5.69511 5.20144 5.69511 4.83907 5.94981 4.78345C9.04541 4.10559 9.87116 3.28117 10.5499 0.190716C10.6058 -0.0635719 10.9688 -0.0635719 11.0245 0.190716C11.7034 3.28134 12.5292 4.10576 15.6246 4.78345C15.8793 4.83924 15.8793 5.20161 15.6246 5.25723Z" fill={color}/>
      <path d="M8.8865 10.4489C6.99274 10.8635 6.48755 11.3679 6.07222 13.2586C6.03806 13.4143 5.81612 13.4143 5.78179 13.2586C5.36646 11.3679 4.86127 10.8635 2.96751 10.4489C2.81161 10.4148 2.81161 10.1932 2.96751 10.1589C4.86127 9.74425 5.36646 9.23988 5.78179 7.34916C5.81595 7.19351 6.03789 7.19351 6.07222 7.34916C6.48755 9.23988 6.99274 9.74425 8.8865 10.1589C9.0424 10.193 9.0424 10.4146 8.8865 10.4489Z" fill={color}/>
      <path d="M11.6174 16H2.90834C1.30466 16 0 14.6974 0 13.0963V4.40123C0 2.80013 1.30466 1.49756 2.90834 1.49756H6.77434C7.13711 1.49756 7.43122 1.7912 7.43122 2.15339C7.43122 2.51558 7.13711 2.80922 6.77434 2.80922H2.90834C2.02917 2.80922 1.31377 3.52329 1.31377 4.40123V13.0963C1.31377 13.9741 2.02899 14.6883 2.90834 14.6883H11.6174C12.4966 14.6883 13.212 13.9743 13.212 13.0963V8.55797C13.212 8.19578 13.5061 7.90214 13.8689 7.90214C14.2317 7.90214 14.5258 8.19578 14.5258 8.55797V13.0963C14.5258 14.6974 13.2211 16 11.6174 16Z" fill={color}/>
    </svg>
  ),
  Package: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="16.5" x2="7.5" y1="9.4" y2="4.21" />
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" x2="12" y1="22.08" y2="12" />
    </svg>
  ),
  Paperclip: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  ),
  PhoneCall: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M15.05 5A5 5 0 0 1 19 8.95M15.05 1A9 9 0 0 1 23 8.94m-1 7.98v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  PhoneForwarded: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="19 1 23 5 19 9" />
      <line x1="15" x2="23" y1="5" y2="5" />
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  PhoneIncoming: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="16 2 16 8 22 8" />
      <line x1="23" x2="16" y1="1" y2="8" />
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  PhoneMissed: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="23" x2="17" y1="1" y2="7" />
      <line x1="17" x2="23" y1="1" y2="7" />
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  PhoneOff: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
      <line x1="23" x2="1" y1="1" y2="23" />
    </svg>
  ),
  PhoneeOutgoing: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="23 7 23 1 17 1" />
      <line x1="16" x2="23" y1="8" y2="1" />
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  Phone: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  PieChart: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
      <path d="M22 12A10 10 0 0 0 12 2v10z" />
    </svg>
  ),
  PlusCircle: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="16" />
      <line x1="8" x2="16" y1="12" y2="12" />
    </svg>
  ),
  PlusSquare: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect height="18" rx="2" ry="2" width="18" x="3" y="3" />
      <line x1="12" x2="12" y1="8" y2="16" />
      <line x1="8" x2="16" y1="12" y2="12" />
    </svg>
  ),
  Plus: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="12" x2="12" y1="5" y2="19" />
      <line x1="5" x2="19" y1="12" y2="12" />
    </svg>
  ),
  Pocket: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M4 3h16a2 2 0 0 1 2 2v6a10 10 0 0 1-10 10A10 10 0 0 1 2 11V5a2 2 0 0 1 2-2z" />
      <polyline points="8 10 12 14 16 10" />
    </svg>
  ),
  Printer: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect height="8" width="12" x="6" y="14" />
    </svg>
  ),
  RefreshCcw: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="1 4 1 10 7 10" />
      <polyline points="23 20 23 14 17 14" />
      <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
    </svg>
  ),
  RefreshCw: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  ),
  RotateCcw: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  ),
  RotateCw: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  ),
  Rss: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M4 11a9 9 0 0 1 9 9" />
      <path d="M4 4a16 16 0 0 1 16 16" />
      <circle cx="5" cy="19" r="1" />
    </svg>
  ),
  Save: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  Scissors: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="6" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <line x1="20" x2="8.12" y1="4" y2="15.88" />
      <line x1="14.47" x2="20" y1="14.48" y2="20" />
      <line x1="8.12" x2="12" y1="8.12" y2="12" />
    </svg>
  ),
  Search: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" x2="16.65" y1="21" y2="16.65" />
    </svg>
  ),
  Send: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="22" x2="11" y1="2" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  Server: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect height="8" rx="2" ry="2" width="20" x="2" y="2" />
      <rect height="8" rx="2" ry="2" width="20" x="2" y="14" />
      <line x1="6" x2="6.01" y1="6" y2="6" />
      <line x1="6" x2="6.01" y1="18" y2="18" />
    </svg>
  ),
  Settings: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  Settings2: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M3 12H21"/>
      <path d="M3 6H21"/>
      <path d="M3 18H21"/>
      <circle cx="8" cy="6" r="2" fill="white"/>
      <circle cx="16" cy="12" r="2" fill="white"/>
      <circle cx="8" cy="18" r="2" fill="white"/>
    </svg>
  ),
  Share2: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" x2="15.42" y1="13.51" y2="17.49" />
      <line x1="15.41" x2="8.59" y1="6.51" y2="10.49" />
    </svg>
  ),
  Share: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" x2="12" y1="2" y2="15" />
    </svg>
  ),
  ShieldOff: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M19.69 14a6.9 6.9 0 0 0 .31-2V5l-8-3-3.16 1.18" />
      <path d="M4.73 4.73L4 5v7c0 6 8 10 8 10a20.29 20.29 0 0 0 5.62-4.38" />
      <line x1="1" x2="23" y1="1" y2="23" />
    </svg>
  ),
  Shield: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  ShoppingBag: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" x2="21" y1="6" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  ),
  ShoppingCart: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  ),
  Slash: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" x2="19.07" y1="4.93" y2="19.07" />
    </svg>
  ),
  Sliders: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="4" x2="4" y1="21" y2="14" />
      <line x1="4" x2="4" y1="10" y2="3" />
      <line x1="12" x2="12" y1="21" y2="12" />
      <line x1="12" x2="12" y1="8" y2="3" />
      <line x1="20" x2="20" y1="21" y2="16" />
      <line x1="20" x2="20" y1="12" y2="3" />
      <line x1="1" x2="7" y1="14" y2="14" />
      <line x1="9" x2="15" y1="8" y2="8" />
      <line x1="17" x2="23" y1="16" y2="16" />
    </svg>
  ),
  SmartPhone: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect height="20" rx="2" ry="2" width="14" x="5" y="2" />
      <line x1="12" x2="12.01" y1="18" y2="18" />
    </svg>
  ),
  Smile: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <line x1="9" x2="9.01" y1="9" y2="9" />
      <line x1="15" x2="15.01" y1="9" y2="9" />
    </svg>
  ),
  Star: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  Sun: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="5" />
      <line x1="12" x2="12" y1="1" y2="3" />
      <line x1="12" x2="12" y1="21" y2="23" />
      <line x1="4.22" x2="5.64" y1="4.22" y2="5.64" />
      <line x1="18.36" x2="19.78" y1="18.36" y2="19.78" />
      <line x1="1" x2="3" y1="12" y2="12" />
      <line x1="21" x2="23" y1="12" y2="12" />
      <line x1="4.22" x2="5.64" y1="19.78" y2="18.36" />
      <line x1="18.36" x2="19.78" y1="5.64" y2="4.22" />
    </svg>
  ),
  Table: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18" />
    </svg>
  ),
  Tablet: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect height="20" rx="2" ry="2" width="16" x="4" y="2" />
      <line x1="12" x2="12.01" y1="18" y2="18" />
    </svg>
  ),
  Target: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  ),
  Terminal: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="4 17 10 11 4 5" />
      <line x1="12" x2="20" y1="19" y2="19" />
    </svg>
  ),
  ThumbsDown: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
    </svg>
  ),
  ThumbsUp: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
    </svg>
  ),
  Tool: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  ),
  Trash2: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" x2="10" y1="11" y2="17" />
      <line x1="14" x2="14" y1="11" y2="17" />
    </svg>
  ),
  TrendingDown: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
      <polyline points="17 18 23 18 23 12" />
    </svg>
  ),
  TrendingUp: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  TrendingUpChart: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 20 20"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M1 1V16C1 17.6569 2.34315 19 4 19H19"/>
      <path d="M18.8752 5.5L12.9971 11.3781L9.90332 8.28438L5.2627 12.925"/>
      <path d="M15.1626 5.5H18.8751V9.2125"/>
    </svg>
  ),
  Triangle: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    </svg>
  ),
  Truck: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect height="13" width="15" x="1" y="3" />
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  ),
  Umbrella: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M23 12a11.05 11.05 0 0 0-22 0zm-5 7a3 3 0 0 1-6 0v-7"/>
    </svg>
  ),
  Underline: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" />
      <line x1="4" x2="20" y1="21" y2="21" />
    </svg>
  ),
  Unlock: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect height="11" rx="2" ry="2" width="18" x="3" y="11" />
      <path d="M7 11V7a5 5 0 0 1 9.9-1" />
    </svg>
  ),
  UploadCloud: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <polyline points="16 16 12 12 8 16" />
      <line x1="12" x2="12" y1="12" y2="21" />
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
      <polyline points="16 16 12 12 8 16" />
    </svg>
  ),
  Upload: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" x2="12" y1="3" y2="15" />
    </svg>
  ),
  UserCheck: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <polyline points="17 11 19 13 23 9" />
    </svg>
  ),
  UserMinus: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="23" x2="17" y1="11" y2="11" />
    </svg>
  ),
  UserPlus: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="20" x2="20" y1="8" y2="14" />
      <line x1="23" x2="17" y1="11" y2="11" />
    </svg>
  ),
  UserX: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="18" x2="23" y1="8" y2="13" />
      <line x1="23" x2="18" y1="8" y2="13" />
    </svg>
  ),
  User: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Users: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  WifiOff: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="1" x2="23" y1="1" y2="23" />
      <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
      <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
      <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
      <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <line x1="12" x2="12.01" y1="20" y2="20" />
    </svg>
  ),
  Wifi: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M5 12.55a11 11 0 0 1 14.08 0" />
      <path d="M1.42 9a16 16 0 0 1 21.16 0" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <line x1="12" x2="12.01" y1="20" y2="20" />
    </svg>
  ),
  XCircle: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="15" x2="9" y1="9" y2="15" />
      <line x1="9" x2="15" y1="9" y2="15" />
    </svg>
  ),
  XSquare: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <rect height="18" rx="2" ry="2" width="18" x="3" y="3" />
      <line x1="9" x2="15" y1="9" y2="15" />
      <line x1="15" x2="9" y1="9" y2="15" />
    </svg>
  ),
  X: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <line x1="18" x2="6" y1="6" y2="18" />
      <line x1="6" x2="18" y1="6" y2="18" />
    </svg>
  ),
  ZoomIn: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" x2="16.65" y1="21" y2="16.65" />
      <line x1="11" x2="11" y1="8" y2="14" />
      <line x1="8" x2="14" y1="11" y2="11" />
    </svg>
  ),
  ZoomOut: ({ color = 'currentColor', size = 24, ...rest }: IconProps) => (
    <svg
      fill="none"
      height={size}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" x2="16.65" y1="21" y2="16.65" />
      <line x1="8" x2="14" y1="11" y2="11" />
    </svg>
  ),
};

export { Icons, CustomIconKeys };
